// src/utils/excelParser.js
import * as XLSX from "xlsx";

/* =========================================================
   ONE-HERO helpers
   ========================================================= */
const COL = (c) => (row) => `${c}${row}`;

function getCell(ws, addr) {
  const cell = ws[addr];
  if (!cell) return null;
  return (cell.v !== undefined) ? cell.v : (cell.w ?? null);
}

function toNum(x) {
  if (x === null || x === undefined) return null;
  const n = typeof x === 'string' ? Number(String(x).replace(/[% ,]/g, '')) : Number(x);
  return Number.isFinite(n) ? n : null;
}

/** อ่านบล็อค 3 คอลัมน์: [ชื่อ, total, win] หรือ [ชื่อ, total, wr] */
function parseBlock3(ws, { colA, colB, colC, rowStart, rowEnd, position }) {
  const A = COL(colA), B = COL(colB), C = COL(colC);
  const r0 = Math.min(rowStart, rowEnd), r1 = Math.max(rowStart, rowEnd);
  const rows = [];
  for (let r = r0; r <= r1; r++) {
    const name = String(getCell(ws, A(r)) ?? '').trim();
    if (!name) continue;
    const b = getCell(ws, B(r));
    const c = getCell(ws, C(r));
    const nb = toNum(b);
    const nc = toNum(c);

    let total = null, win = null, winrate = null;
    if (nb !== null && nc !== null) {
      const looksLikeWR01 = nc >= 0 && nc <= 1.01;
      const looksLikeWR100 = (nc > 1 && nc <= 100) && String(c).includes('.');
      const looksLikePctStr = typeof c === 'string' && c.includes('%');
      if (looksLikeWR01 || looksLikeWR100 || looksLikePctStr) {
        total = nb;
        winrate = looksLikeWR01 ? (nc * 100) : (toNum(c));
        const wr = (winrate ?? 0) / 100;
        win = Math.round((total ?? 0) * wr);
      } else {
        if (nb >= nc) { total = nb; win = nc; }
        else { total = nc; win = nb; }
        winrate = (total ? (win / total) * 100 : 0);
      }
    } else if (nb !== null) {
      total = nb; win = 0; winrate = 0;
    } else {
      continue;
    }
    if (!total || total <= 0) continue;
    rows.push({
      hero: name, position,
      total: Math.round(total),
      win: Math.round(win ?? 0),
      winrate: Number.isFinite(winrate) ? +winrate.toFixed(1) : 0
    });
  }
  return rows;
}

/** พิกัดบล็อค ONE-HERO (ใช้กับชีททีม EA/BAC/… ไม่ใช่ Competitor_Hero_stats) */
const ONE_HERO_MAP = {
  EA: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 120, rowEnd: 136 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 120, rowEnd: 137 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 120, rowEnd: 133 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 120, rowEnd: 129 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 120, rowEnd: 134 },
  ],
  BAC: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 113, rowEnd: 126 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 113, rowEnd: 130 },
    { position: 'MID', colA: 'M', colB: 'N', colC: 'O', rowStart: 113, rowEnd: 127 },
    { position: 'ADL', colA: 'S', colB: 'T', colC: 'U', rowStart: 114, rowEnd: 125 },
    { position: 'SUP', colA: 'Y', colB: 'Z', colC: 'AA', rowStart: 113, rowEnd: 132 },
  ],
  BRU: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 175, rowEnd: 186 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 176, rowEnd: 186 },
    { position: 'MID', colA: 'M', colB: 'N', colC: 'O', rowStart: 172, rowEnd: 186 },
    { position: 'ADL', colA: 'S', colB: 'T', colC: 'U', rowStart: 170, rowEnd: 186 },
    { position: 'SUP', colA: 'Y', colB: 'Z', colC: 'AA', rowStart: 180, rowEnd: 186 },
  ],
  KOG: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 100, rowEnd: 116 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 100, rowEnd: 112 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 100, rowEnd: 114 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 100, rowEnd: 109 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 100, rowEnd: 115 },
  ],
  // ===== ทีมใหม่ SUMMER 2026 =====
  SLX: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 100, rowEnd: 120 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 100, rowEnd: 120 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 100, rowEnd: 120 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 100, rowEnd: 120 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 100, rowEnd: 120 },
  ],
  TEN: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 100, rowEnd: 120 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 100, rowEnd: 120 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 100, rowEnd: 120 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 100, rowEnd: 120 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 100, rowEnd: 120 },
  ],
  GODJI: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 100, rowEnd: 120 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 100, rowEnd: 120 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 100, rowEnd: 120 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 100, rowEnd: 120 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 100, rowEnd: 120 },
  ],
  // ทีมอื่นๆ
  TLN: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 130, rowEnd: 146 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 130, rowEnd: 147 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 130, rowEnd: 141 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 130, rowEnd: 144 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 130, rowEnd: 152 },
  ],
  PSG: [
    { position: 'DSL', colA: 'A', colB: 'B', colC: 'C', rowStart: 143, rowEnd: 157 },
    { position: 'JUG', colA: 'G', colB: 'H', colC: 'I', rowStart: 143, rowEnd: 157 },
    { position: 'MID', colA: 'L', colB: 'M', colC: 'N', rowStart: 143, rowEnd: 156 },
    { position: 'ADL', colA: 'Q', colB: 'R', colC: 'S', rowStart: 143, rowEnd: 155 },
    { position: 'SUP', colA: 'V', colB: 'W', colC: 'X', rowStart: 143, rowEnd: 166 },
  ],
};

// รายชื่อทีมหลักตามไฟล์ FS GIRL SUMMER 2026
const SUMMER_2026_TEAMS = [
  "FW", "PSG", "BRU", "HKA", "DCG",
  "ONE", "FPT", "EA", "HD", "SLX", "TEN", "KOG"
];

/* =========================================================
   Date helpers
   ========================================================= */
function ymdFromDate(d) {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}
function excelSerialDateToYMD(serial) {
  if (serial == null || serial === "" || Number.isNaN(Number(serial))) return "";
  const n = Number(serial);
  const tryParseSSF = (opts) => {
    const p = XLSX.SSF?.parse_date_code ? XLSX.SSF.parse_date_code(n, opts) : null;
    if (!p || !p.y || !p.m || !p.d) return null;
    return new Date(Date.UTC(p.y, p.m - 1, p.d));
  };
  let d = tryParseSSF({ date1904: false });
  if (d && d.getUTCFullYear() < 1990) {
    const d2 = tryParseSSF({ date1904: true });
    if (d2 && d2.getUTCFullYear() >= 1990) d = d2;
  }
  if (!d) {
    const MS = 86400000;
    const base1900 = Date.UTC(1899, 11, 30);
    const d1900 = new Date(base1900 + n * MS);
    const base1904 = Date.UTC(1904, 0, 1);
    const d1904 = new Date(base1904 + n * MS);
    d = d1900;
    if (d1900.getUTCFullYear() < 1990 && d1904.getUTCFullYear() >= 1990) d = d1904;
  }
  return ymdFromDate(d);
}
function parseDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return ymdFromDate(new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())));
  }
  if (typeof value === "number" && value > 20000 && value < 80000) {
    return excelSerialDateToYMD(value);
  }
  const s = String(value).trim();
  if (/^\d{8}$/.test(s)) {
    const y2 = +s.slice(0, 4), m2 = +s.slice(4, 6), d2 = +s.slice(6, 8);
    if (y2 >= 2000 && y2 <= 2100 && m2 >= 1 && m2 <= 12 && d2 >= 1 && d2 <= 31) return s;
  }
  const dmY = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmY) {
    const d = +dmY[1], m = +dmY[2], y = +dmY[3];
    if (y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${String(y).padStart(4, "0")}${String(m).padStart(2, "0")}${String(d).padStart(2, "0")}`;
    }
  }
  const yMd = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (yMd) {
    const y = +yMd[1], m = +yMd[2], d = +yMd[3];
    if (y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return `${String(y).padStart(4, "0")}${String(m).padStart(2, "0")}${String(d).padStart(2, "0")}`;
    }
  }
  return "";
}
function formatDateShort(str) {
  if (!str || str.length !== 8) return "-";
  const yyyy = str.slice(0, 4), mm = str.slice(4, 6), dd = str.slice(6, 8);
  return `${dd}/${mm}/${yyyy.slice(2)}`;
}
function buildWeekMap(dates) {
  return [...new Set(dates.filter(Boolean))].sort().reduce((map, d, i) => {
    map[d] = `W${i + 1}`; return map;
  }, {});
}
function cleanHero(name) {
  const s = String(name ?? "").trim();
  const lower = s.toLowerCase();
  if (!s || lower === "nan" || lower.includes("sum") || s.includes("ผลรวม")) return "";
  return s;
}
function isTruthy(v) { return v !== null && v !== undefined && String(v).trim() !== ""; }

/* =========================================================
   Helpers ComboStats (fallback index-based)
   ========================================================= */
const isHeroish = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return false;
  if (parseDate(s)) return false;
  if (/^\d+(\.\d+)?$/.test(s)) return false;
  return true;
};
const findDateColInRange = (row, start, end) => {
  for (let c = start; c <= end; c++) if (parseDate(row[c])) return c;
  return -1;
};
const pickHeroPair = (row, primaryStart, dateCol) => {
  const candidates = [
    [primaryStart, primaryStart + 1],
    [dateCol + 1, dateCol + 2],
    [primaryStart - 1, primaryStart],
  ];
  for (const [a, b] of candidates) {
    if (a === dateCol || b === dateCol) continue;
    if (isHeroish(row[a]) && isHeroish(row[b])) return [a, b];
  }
  return [primaryStart, primaryStart + 1];
};
const pickHeroTriple = (row, primaryStart, dateCol) => {
  const candidates = [
    [primaryStart, primaryStart + 1, primaryStart + 2],
    [dateCol + 1, dateCol + 2, dateCol + 3],
    [primaryStart - 1, primaryStart, primaryStart + 1],
  ];
  for (const [a, b, c] of candidates) {
    if ([a, b, c].includes(dateCol)) continue;
    if (isHeroish(row[a]) && isHeroish(row[b]) && isHeroish(row[c])) return [a, b, c];
  }
  return [primaryStart, primaryStart + 1, primaryStart + 2];
};

/* =========================================================
   Scrim sheet mapping (UPDATED for SUMMER 2026)
   ========================================================= */
function norm(s) { return String(s ?? "").trim().replace(/\s+/g, " ").toLowerCase(); }
function findColIndex(headerRow, ...aliases) {
  const H = headerRow.map(norm), AL = aliases.map(norm);
  for (let i = 0; i < H.length; i++) if (AL.includes(H[i])) return i;
  return -1;
}
function findSeries(headerRow, baseAliases, count) {
  const out = [];
  for (let k = 1; k <= count; k++) {
    let idx = -1;
    for (const base of baseAliases) {
      const candidates = [`${base} ${k}`, `${base}${k}`, `${base}-${k}`, `${base}_${k}`];
      idx = findColIndex(headerRow, ...candidates);
      if (idx !== -1) break;
    }
    out.push(idx);
  }
  return out;
}

function findPrimaryTeamPrefix(headerRow = []) {
  const counts = new Map();
  (headerRow || []).forEach((cell) => {
    const text = norm(cell);
    if (!text) return;
    const m = text.match(/^([a-z0-9]+)\s+(dsl|dark slayer|slayer|jug|jungle|jg|mid|midlane|adl|adc|abyssal|dragon|sup|support|roam|roaming|ban|win)\b/);
    if (!m) return;
    const prefix = m[1];
    if (["op", "opp", "enemy"].includes(prefix)) return;
    counts.set(prefix, (counts.get(prefix) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

function findColIndexWithPrefixes(headerRow, aliases = [], prefixes = []) {
  const base = aliases.map((s) => String(s || "").trim()).filter(Boolean);
  const names = new Set(base);
  (prefixes || []).filter(Boolean).forEach((prefix) => {
    base.forEach((alias) => {
      names.add(`${prefix} ${alias}`);
      names.add(`${prefix}_${alias}`);
      names.add(`${prefix}-${alias}`);
    });
  });
  return findColIndex(headerRow, ...[...names]);
}

function findSeriesWithPrefixes(headerRow, baseAliases, count, prefixes = []) {
  const out = [];
  for (let k = 1; k <= count; k++) {
    const names = new Set();
    for (const base of baseAliases) {
      names.add(`${base} ${k}`);
      names.add(`${base}${k}`);
      names.add(`${base}-${k}`);
      names.add(`${base}_${k}`);
      (prefixes || []).filter(Boolean).forEach((prefix) => {
        names.add(`${prefix} ${base} ${k}`);
        names.add(`${prefix} ${base}${k}`);
        names.add(`${prefix}_${base}_${k}`);
        names.add(`${prefix}-${base}-${k}`);
      });
    }
    out.push(findColIndex(headerRow, ...[...names]));
  }
  return out;
}

function findDraftRecapBanSeries(headerRow = [], side = "team") {
  const out = Array(4).fill(-1);
  const opponentPrefixes = new Set(["op", "opp", "enemy", "b"]);

  (headerRow || []).forEach((cell, idx) => {
    const text = norm(cell).replace(/[_-]+/g, " ");
    if (!text) return;

    const prefixed = text.match(/^([a-z0-9]+)\s+ban\s*([1-4])$/);
    const plain = text.match(/^ban\s*([1-4])$/);
    const banNo = prefixed ? Number(prefixed[2]) : (plain ? Number(plain[1]) : 0);
    if (!banNo) return;

    if (side === "op") {
      if (!prefixed || !opponentPrefixes.has(prefixed[1])) return;
    } else if (prefixed && opponentPrefixes.has(prefixed[1])) {
      return;
    }

    // เจอก่อนชนะ (เหมือน findSeries/findColIndex ที่อื่น) — บางไฟล์มีบล็อกซ้ำ
    // ตั้งหัวว่า "FS Ban 3/4" อีกชุดไว้ขวาสุด แต่ข้างในเก็บตำแหน่ง (JUG/ROAM)
    // ถ้าให้ตัวหลังทับ ช่องแบน 3-4 จะกลายเป็นชื่อตำแหน่ง
    if (out[banNo - 1] === -1) out[banNo - 1] = idx;
  });

  return out;
}

function inferColumnByValues(rows, predicate, { exclude = [], minScore = 3, maxRows = 300 } = {}) {
  const blocked = new Set((exclude || []).filter((i) => Number.isInteger(i) && i >= 0));
  const maxCols = Math.max(
    (rows?.[0] || []).length || 0,
    ...(rows || []).slice(1, maxRows).map((r) => (r || []).length || 0)
  );
  let bestCol = -1;
  let bestScore = 0;

  for (let c = 0; c < maxCols; c++) {
    if (blocked.has(c)) continue;
    let score = 0;
    const uniques = new Set();

    for (let r = 1; r < Math.min((rows || []).length, maxRows); r++) {
      const value = rows?.[r]?.[c];
      if (!predicate(value, rows?.[r], c)) continue;
      score += 1;
      uniques.add(String(value).trim());
    }

    score += Math.min(uniques.size, 12) * 0.35;
    if (score > bestScore) {
      bestScore = score;
      bestCol = c;
    }
  }

  return bestScore >= minScore ? bestCol : -1;
}

const SCRIM_ROLE_ORDER = ["DSL", "JUG", "MID", "ADL", "SUP"];

function detectRoleColumnMap(headerRow, side = "team") {
  const roleCols = { DSL: -1, JUG: -1, MID: -1, ADL: -1, SUP: -1 };
  const isOpponentHeader = (value = "") => /^(op|opp|enemy)\b/.test(value);
  const blocked = /(?:\bban\b|\bpick\b|\bposition\b|\bwin\b|\bmatch\b|\bresult\b|\btype\b|\bdate\b|\bweek\b|\bgame\b|\bside\b|\btime\b|\bend\b)/;

  (headerRow || []).forEach((cell, index) => {
    const c = norm(cell);
    if (!c || blocked.test(c)) return;

    const isOpponent = isOpponentHeader(c);
    if (side === "team" ? isOpponent : !isOpponent) return;

    if (roleCols.DSL < 0 && /(^|\s)(dsl|dark slayer|slayer)$/.test(c)) roleCols.DSL = index;
    if (roleCols.JUG < 0 && /(^|\s)(jug|jungle|jg)$/.test(c)) roleCols.JUG = index;
    if (roleCols.MID < 0 && /(^|\s)(mid|midlane)$/.test(c)) roleCols.MID = index;
    if (roleCols.ADL < 0 && /(^|\s)(adl|adc|abyssal|dragon)$/.test(c)) roleCols.ADL = index;
    if (roleCols.SUP < 0 && /(^|\s)(sup|support|roam|roaming)$/.test(c)) roleCols.SUP = index;
  });

  return roleCols;
}

function normalizeRoleToken(pos = "") {
  const token = String(pos || "").trim().toUpperCase();
  if (token === "JGL" || token === "JUNGLE") return "JUG";
  if (token === "MIDLANE") return "MID";
  if (token === "ROAMING" || token === "SUPPORT") return "SUP";
  return token;
}

function buildRolePickMap({ row = [], roleCols = {}, picks = [], positions = [] } = {}) {
  const out = { DSL: "", JUG: "", MID: "", ADL: "", SUP: "" };

  SCRIM_ROLE_ORDER.forEach((role) => {
    const colIndex = Number(roleCols?.[role] ?? -1);
    if (colIndex >= 0) {
      out[role] = cleanHero(row[colIndex]);
    }
  });

  (positions || []).forEach((pos, index) => {
    const role = normalizeRoleToken(pos);
    if (!SCRIM_ROLE_ORDER.includes(role)) return;
    if (out[role]) return;
    out[role] = cleanHero((picks || [])[index]);
  });

  return out;
}

function buildScrimIndexMap(rows) {
  const header = rows[0] || [];
  const teamPrefix = findPrimaryTeamPrefix(header);
  const teamPrefixes = teamPrefix ? [teamPrefix] : [];
  const inferDateCol = () => {
    const maxCols = Math.max(
      header.length || 0,
      ...rows.slice(1, 300).map((r) => (r || []).length || 0)
    );
    let bestCol = -1;
    let bestScore = 0;
    for (let c = 0; c < maxCols; c++) {
      let score = 0;
      for (let r = 1; r < Math.min(rows.length, 300); r++) {
        if (parseDate(rows[r]?.[c])) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        bestCol = c;
      }
    }
    return bestScore >= 3 ? bestCol : -1;
  };
  const idx = {
    date: findColIndex(header, "date"),
    week: findColIndex(header, "week"),
    game: findColIndex(header, "game"),
    competitor: findColIndex(header, "competitor", "team b", "team a"),
    side: findColIndex(header, "fs side", "side", "a side", "b side"),
    matchWin: findColIndexWithPrefixes(header, ["match win", "result", "win", "fs win", "a win"], teamPrefixes),
    fsWin: findColIndexWithPrefixes(header, ["fs win", "fswin", "our win", "match win", "win"], teamPrefixes),
    type: findColIndex(header, "type"),
    endType: findColIndex(header, "end type"),
    time: findColIndex(header, "time", "duration"),
    minute: findColIndex(header, "minute"),
    fsBan: findSeriesWithPrefixes(header, ["fs ban", "ban", "a ban"], 4, teamPrefixes),
    fsPick: findSeries(header, ["fs pick", "pick", "a pick"], 5),
    fsPos: findSeries(header, ["fs position", "position", "position fs", "pos", "a position"], 5),
    opBan: findSeries(header, ["op ban", "enemy ban", "opp ban", "b ban"], 4),
    opPick: findSeries(header, ["op pick", "enemy pick", "opp pick", "b pick"], 5),
    opPos: findSeries(header, ["op position", "enemy position", "opp position", "position op", "b position"], 5),
  };
  idx.fsRoleCols = detectRoleColumnMap(header, "team");
  idx.opRoleCols = detectRoleColumnMap(header, "op");
  if (idx.date === -1) idx.date = inferDateCol();
  if (idx.endType === -1) idx.endType = header.findIndex(h => norm(h).startsWith("end type"));
  if (idx.type === -1) {
    idx.type = inferColumnByValues(
      rows,
      (value) => ["scrim", "rpl", "official", "match"].includes(norm(value)),
      { exclude: [idx.date, idx.week, idx.game], minScore: 2 }
    );
  }
  if (idx.competitor === -1) {
    idx.competitor = inferColumnByValues(
      rows,
      (value) => {
        const s = String(value ?? "").trim();
        if (!s || parseDate(s) || /^\d+(\.\d+)?$/.test(s)) return false;
        if (["scrim", "rpl", "official", "match", "win", "lose"].includes(norm(s))) return false;
        return /^[A-Z0-9][A-Z0-9 ._-]{1,8}$/.test(s);
      },
      {
        exclude: [
          idx.date, idx.week, idx.game, idx.type, idx.matchWin, idx.fsWin, idx.side,
          ...(idx.fsBan || []), ...(idx.opBan || []), ...(idx.fsPick || []), ...(idx.opPick || []),
          ...(idx.fsPos || []), ...(idx.opPos || []), ...Object.values(idx.fsRoleCols || {}), ...Object.values(idx.opRoleCols || {})
        ],
        minScore: 2,
      }
    );
  }
  return idx;
}
const ALL_SCRIM_SHEET_NAMES = ["All_Scrim_Stats", "All_Scrim"];
const ALL_SCRIM_VISIBLE_START_ROW = 1230; // 1-based Excel row

function isAllScrimSheetName(sheetName) {
  const key = String(sheetName || "").trim().toLowerCase();
  return ALL_SCRIM_SHEET_NAMES.some((n) => n.toLowerCase() === key);
}

function constrainRowsToVisibleAllScrim(rows, sheetName) {
  if (!isAllScrimSheetName(sheetName)) return rows;
  if (!Array.isArray(rows) || rows.length <= 1) return rows || [];
  // The row-1230 cutoff skips the frozen archive block at the top of the master
  // file (FS ROV APL 2026-x), keeping only the current-season window. But an
  // uploaded file with the SAME columns yet fewer rows (a fresh / trimmed /
  // different file) has no archive to skip — slicing would drop everything and
  // show an empty page. Auto-fallback: only apply the cutoff when the sheet is
  // actually long enough to reach it; otherwise use every data row.
  if (rows.length < ALL_SCRIM_VISIBLE_START_ROW) return rows;
  const header = rows[0];
  const startIdx = Math.max(1, ALL_SCRIM_VISIBLE_START_ROW - 1);
  return [header, ...rows.slice(startIdx)];
}

/* ชีทนั้นเป็น "log ต่อเกม" จริงไหม — บางไฟล์มีชีทชื่อตรงลิสต์แต่ข้างในเป็นตารางสรุป
   (เช่น RPL_Stats ของไฟล์ทีมหญิงที่ไม่มีหัวตารางเลย) ถ้าเลือกอันนั้นจะได้ 0 เกม
   ทั้งแอป ทั้งที่ log จริงอยู่อีกชีทหนึ่ง */
function looksLikeGameLog(workbook, sheetName) {
  const ws = workbook?.Sheets?.[sheetName];
  if (!ws) return false;
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (rows.length < 2) return false;

  const idx = buildScrimIndexMap(rows);
  const hasPicks =
    (idx.fsPick || []).some((i) => i >= 0) ||
    Object.values(idx.fsRoleCols || {}).some((i) => i >= 0);
  if (!hasPicks || idx.date < 0) return false;

  // ต้องมีแถวที่เป็นเกมจริง ไม่ใช่หัวตาราง/แถวว่าง
  const hasGameRow = rows.slice(1).some(
    (r) => parseDate(r[idx.date]) && cleanHero(r[idx.fsPick?.find((i) => i >= 0) ?? -1] ?? "")
  );
  if (!hasGameRow) return false;

  // log ของเรา (คอลัมน์ขึ้นต้นด้วย FS) หรือ log ลีกกลางแบบ Team A/Team B
  // อันหลังฝั่ง "เรา" จะกลายเป็นทีม A ที่ไม่ใช่ FS — เก็บไว้เป็นตัวเลือกสำรอง
  const ours = (rows[0] || []).some((h) => /^fs\b/i.test(String(h || "").trim()));
  return ours ? "ours" : "league";
}

function pickStatSheetName(workbook) {
  const sheets = workbook.SheetNames || [];
  const normSheet = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, "");
  const exact = [
    "Girl_Scrim",     // FS GIRL workbook primary
    "APL_Scrim",      // APL 2026 primary
    "RPL_Scrim",      // SUMMER 2026 primary
    "APL_Stats",
    "RPL_Stats",
    "RPL_Stat",
    "RPL_Winter",
    "All_Scrim_Stats",
    "All_Scrim",       // APL 2026-2 renamed
    "Winter_Scrim_Stats",
    "AIC_Scrim",
    "Summer_2026_Scrim",
  ];
  const candidates = exact.filter((name) => sheets.includes(name));

  // บางไฟล์สะกดชื่อชีทต่างกัน (เว้นวรรค/ขีดล่าง) — จับแบบตัดอักขระคั่นออกเพิ่มให้ด้วย
  for (const key of exact.map(normSheet)) {
    const hit = sheets.find((s) => normSheet(s) === key);
    if (hit && !candidates.includes(hit)) candidates.push(hit);
  }

  const low = sheets.map((s) => s.toLowerCase());
  const pickBy = (kw) => {
    const i = low.findIndex((n) => n.includes("stats") && n.includes(kw));
    return i >= 0 ? sheets[i] : "";
  };
  const pickSheetBy = (kw) => {
    const i = low.findIndex((n) => n.includes(kw));
    return i >= 0 ? sheets[i] : "";
  };
  const fuzzy = [pickBy("apl"), pickBy("rpl"), pickBy("winter"), pickSheetBy("scrim")].filter(Boolean);
  const ordered = [...new Set([...candidates, ...fuzzy])];
  if (!ordered.length) return "";

  // ลำดับเดิมมาก่อนเสมอ แค่ข้ามอันที่ไม่ใช่ log ต่อเกม
  // และเลือก log ที่เป็นมุมมองของเราก่อน log ลีกกลาง
  const kinds = ordered.map((name) => [name, looksLikeGameLog(workbook, name)]);
  const ours = kinds.find(([, kind]) => kind === "ours");
  const league = kinds.find(([, kind]) => kind === "league");
  return (ours || league || [ordered[0]])[0];
}

function resolveWinLoss(matchWinRaw, fsWinRaw) {
  const n = Number(fsWinRaw);
  if (Number.isFinite(n) && (n === 0 || n === 1)) {
    return n === 1 ? { result: "Win", win: 1, lose: 0 } : { result: "Lose", win: 0, lose: 1 };
  }
  const mw = String(matchWinRaw ?? "").trim();
  if (["FS", "Win", "1", "win", "WIN"].includes(mw) || Number(mw) === 1) {
    return { result: "Win", win: 1, lose: 0 };
  }
  return { result: "Lose", win: 0, lose: 1 };
}
function normalizeWeekValue(w) {
  const s = String(w ?? "").trim();
  if (!s) return "";
  const m = s.match(/^w?\s*(\d+)$/i);
  if (m) return `W${Number(m[1])}`;
  return s;
}

function buildBanPickIndexMap(rows) {
  const header = rows[0] || [];
  const teamPrefix = findPrimaryTeamPrefix(header);
  const teamPrefixes = teamPrefix ? [teamPrefix] : [];
  const idx = {
    matchWin: findColIndexWithPrefixes(header, ["match win", "result", "win", "fs win"], teamPrefixes),
    fsWin: findColIndexWithPrefixes(header, ["fs win", "fswin", "our win", "win"], teamPrefixes),
    fsBan: findSeriesWithPrefixes(header, ["fs ban", "ban", "a ban"], 4, teamPrefixes),
    fsPick: findSeries(header, ["fs pick", "pick", "a pick"], 5),
    opBan: findSeries(header, ["op ban", "enemy ban", "opp ban", "b ban"], 4),
    opPick: findSeries(header, ["op pick", "enemy pick", "opp pick", "b pick"], 5),
  };
  idx.fsRoleCols = detectRoleColumnMap(header, "team");
  idx.opRoleCols = detectRoleColumnMap(header, "op");
  return idx;
}

/* =========================================================
   Public: parse scrim games
   ========================================================= */
// Home overview reads the cumulative All_Scrim log — the SAME sheet the Team
// Performance overview reads — so the two headline figures (games + win rate)
// agree. Fall back to the generic stat sheet only when no All_Scrim sheet
// exists. Mirrors the sheet preference in parseFsTeamReport().
function pickHomeScrimSheetName(wb) {
  if (wb?.Sheets?.["All_Scrim"]) return "All_Scrim";
  if (wb?.Sheets?.["All_Scrim_Stats"]) return "All_Scrim_Stats";
  return pickStatSheetName(wb);
}

// Pure workbook → game[] core (testable; the File wrapper below just decodes).
// opts.preferAllScrim makes the Home overview read All_Scrim and apply the same
// row-validity rule as parseFsTeamReport so both pages count the same games.
export function parseScrimStatsFromWorkbook(wb, opts = {}) {
  const { preferAllScrim = false } = opts;
  const sheetName = preferAllScrim ? pickHomeScrimSheetName(wb) : pickStatSheetName(wb);
  if (!sheetName) { console.warn("ไม่พบชีทสถิติ"); return []; }
  const ws = wb.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const rows = constrainRowsToVisibleAllScrim(rawRows, sheetName);
  if (!rows.length) return [];
  const allScrim = isAllScrimSheetName(sheetName);

  const idx = buildScrimIndexMap(rows);
  const mainRows = rows.slice(1).map(r => ({
    Date: idx.date >= 0 ? r[idx.date] : "",
    Week: idx.week >= 0 ? r[idx.week] : "",
    Competitor: idx.competitor >= 0 ? r[idx.competitor] : "",
    MatchWin: idx.matchWin >= 0 ? r[idx.matchWin] : "",
    FSWin: idx.fsWin >= 0 ? r[idx.fsWin] : "",
    Type: idx.type >= 0 ? r[idx.type] : "",
    TIME: idx.time >= 0 ? r[idx.time] : "",
    Side: idx.side >= 0 ? r[idx.side] : "",
    EndType: idx.endType >= 0 ? r[idx.endType] : "",
    fsBanArr: (idx.fsBan || []).map(i => i >= 0 ? String(r[i] ?? "").trim() : "").filter(Boolean),
    fsPickArr: (
      (idx.fsPick || []).some((i) => i >= 0)
        ? (idx.fsPick || []).map((i) => i >= 0 ? String(r[i] ?? "").trim() : "")
        : SCRIM_ROLE_ORDER.map((role) => {
            const roleIndex = idx.fsRoleCols?.[role] ?? -1;
            return roleIndex >= 0 ? String(r[roleIndex] ?? "").trim() : "";
          })
    ).filter(Boolean),
    raw: r,
  }));

  const allDates = mainRows.map(r => parseDate(r.Date)).filter(Boolean);
  const weekMap = buildWeekMap(allDates);

  const parsed = [];
  for (const r of mainRows) {
    const D = parseDate(r.Date);
    if (!D) continue;

    let competitor = String(r.Competitor ?? "").trim();
    if (allScrim) {
      // Mirror parseFsTeamReport: blank out date-/number-like competitor cells,
      // then keep only rows that carry a real game signal (type / side /
      // resolved result). This drops the spacer rows so the Home overview's
      // game count matches the Team Performance overview exactly.
      if (_looksLikeDate(competitor) || /^\d+$/.test(competitor)) competitor = "";
      const sideRaw = String(r.Side ?? "").trim();
      const hasSide = /^blue$/i.test(sideRaw) || /^red$/i.test(sideRaw);
      const hasType = String(r.Type ?? "").trim() !== "";
      const fsWinKnown =
        Number(r.FSWin) === 0 || Number(r.FSWin) === 1 || String(r.MatchWin ?? "").trim() !== "";
      if (!(hasType || hasSide || fsWinKnown)) continue;
    }

    const weekSheet = String(r.Week ?? "").trim();
    const week = normalizeWeekValue(weekSheet) || normalizeWeekValue(weekMap[D]) || "-";
    const { result, win, lose } = resolveWinLoss(r.MatchWin, r.FSWin);
    parsed.push({
      Date: D, dateStr: formatDateShort(D), week,
      side: r.Side || "", competitor: competitor || "ไม่พบข้อมูล",
      type: r.Type || "ไม่พบข้อมูล", endType: r.EndType || "", time: r.TIME || "",
      fsBan: r.fsBanArr.join(", "), fsPick: r.fsPickArr.join(", "),
      result, win, lose, match: r.MatchWin, raw: r.raw,
    });
  }

  return parsed;
}

export function parseScrimStatsFromExcel(file, setGames, opts = {}) {
  const reader = new FileReader();
  reader.onerror = () => { console.warn("อ่านไฟล์ไม่สำเร็จ"); setGames([]); };
  reader.onload = e => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });
    setGames(parseScrimStatsFromWorkbook(wb, opts));
  };
  reader.readAsArrayBuffer(file);
}

/* =========================================================
   Public: parse games + tier
   ========================================================= */
export function parseGamesFromExcel(file, setGames, setTierData) {
  const reader = new FileReader();
  reader.onerror = () => {
    console.warn("อ่านไฟล์ไม่สำเร็จ");
    setGames([]);
    if (setTierData) setTierData([]);
  };
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });
    const sheetName = pickStatSheetName(wb);
    if (!sheetName) {
      console.warn("ไม่พบชีทสถิติ");
      setGames([]);
      if (setTierData) setTierData([]);
      return;
    }

    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!rows.length) {
      setGames([]);
      if (setTierData) setTierData([]);
      return;
    }

    const parsedGames = buildGamesFromStatRows(rows);
    setGames(parsedGames);

    if (setTierData) setTierData(buildTierDataFromGames(parsedGames));
  };
  reader.readAsArrayBuffer(file);
}

/* แถวชีทสถิติ (header + data) → games[] ที่มี pick/position/ban ของทั้งสองฝั่ง
   แยกออกมาให้เรียกซ้ำได้กับชีทอื่นที่คอลัมน์เหมือนกัน (เช่น All_Scrim ที่เป็น log ทั้งซีซั่น) */
function buildGamesFromStatRows(rows) {
    if (!Array.isArray(rows) || rows.length < 2) return [];
    const idx = buildScrimIndexMap(rows);
    const draftRecapBanIdx = {
      fsBan: findDraftRecapBanSeries(rows[0], "team"),
      opBan: findDraftRecapBanSeries(rows[0], "op"),
    };

    const mainRows = rows.slice(1).map(r => {
      const fsPickArr = [], fsPosArr = [], fsBanArr = [], opBanArr = [], opPickArr = [], opPosArr = [];
      for (let i = 0; i < SCRIM_ROLE_ORDER.length; i++) {
        const role = SCRIM_ROLE_ORDER[i];
        const pIdx = (idx.fsPick && idx.fsPick[i] >= 0)
          ? idx.fsPick[i]
          : (idx.fsRoleCols?.[role] ?? -1);
        const sIdx = (idx.fsPos && idx.fsPos[i] >= 0) ? idx.fsPos[i] : -1;
        fsPickArr.push(cleanHero(pIdx >= 0 ? r[pIdx] : ""));
        fsPosArr.push(sIdx >= 0 ? String(r[sIdx] ?? "").trim() : (pIdx >= 0 ? role : ""));
      }
      for (let i = 0; i < 4; i++) {
        const bIdx = (idx.fsBan && idx.fsBan[i] >= 0) ? idx.fsBan[i] : -1;
        fsBanArr.push(cleanHero(bIdx >= 0 ? r[bIdx] : ""));
        const obIdx = (idx.opBan && idx.opBan[i] >= 0) ? idx.opBan[i] : -1;
        opBanArr.push(cleanHero(obIdx >= 0 ? r[obIdx] : ""));
      }
      for (let i = 0; i < SCRIM_ROLE_ORDER.length; i++) {
        const role = SCRIM_ROLE_ORDER[i];
        const pIdx = (idx.opPick && idx.opPick[i] >= 0)
          ? idx.opPick[i]
          : (idx.opRoleCols?.[role] ?? -1);
        const sIdx = (idx.opPos && idx.opPos[i] >= 0) ? idx.opPos[i] : -1;
        opPickArr.push(cleanHero(pIdx >= 0 ? r[pIdx] : ""));
        opPosArr.push(sIdx >= 0 ? String(r[sIdx] ?? "").trim() : (pIdx >= 0 ? role : ""));
      }
      const side = String(idx.side >= 0 ? r[idx.side] : "").trim().toLowerCase();

      return {
        Date: idx.date >= 0 ? r[idx.date] : "",
        Week: idx.week >= 0 ? r[idx.week] : "",
        Competitor: idx.competitor >= 0 ? r[idx.competitor] : "",
        "Match Win": idx.matchWin >= 0 ? r[idx.matchWin] : "",
        FSWin: idx.fsWin >= 0 ? r[idx.fsWin] : "",
        Type: idx.type >= 0 ? r[idx.type] : "",
        TIME: idx.time >= 0 ? r[idx.time] : "",
        Side: side,
        EndType: idx.endType >= 0 ? r[idx.endType] : "",
        fsPick: fsPickArr, fsPosition: fsPosArr, fsBan: fsBanArr,
        opBan: opBanArr, opPick: opPickArr, opPosition: opPosArr,
        draftRecapFsBan: draftRecapBanIdx.fsBan.map((c) => cleanHero(c >= 0 ? r[c] : "")),
        draftRecapOpBan: draftRecapBanIdx.opBan.map((c) => cleanHero(c >= 0 ? r[c] : "")),
        raw: r,
      };
    });

    const allDates = mainRows.map((r) => parseDate(r.Date)).filter(Boolean);
    const weekMap = buildWeekMap(allDates);

    const parsedGames = mainRows.map((r) => {
      const D = parseDate(r.Date);
      const weekSheet = String(r.Week ?? "").trim();
      const week = normalizeWeekValue(weekSheet) || normalizeWeekValue(weekMap[D]) || "-";
      const { result, win, lose } = resolveWinLoss(r["Match Win"], r.FSWin);
      const fsByRole = buildRolePickMap({
        row: r.raw,
        roleCols: idx.fsRoleCols,
        picks: r.fsPick,
        positions: r.fsPosition,
      });
      const opByRole = buildRolePickMap({
        row: r.raw,
        roleCols: idx.opRoleCols,
        picks: r.opPick,
        positions: r.opPosition,
      });
      return {
        Date: D, dateStr: formatDateShort(D), week, side: r.Side,
        competitor: r.Competitor || "ไม่พบข้อมูล", type: r.Type || "ไม่พบข้อมูล",
        endType: r.EndType || "", time: r.TIME || "",
        fsPick: r.fsPick, fsPosition: r.fsPosition, fsBan: r.fsBan,
        opBan: r.opBan, opPick: r.opPick, opPosition: r.opPosition,
        draftRecapFsBan: r.draftRecapFsBan,
        draftRecapOpBan: r.draftRecapOpBan,
        fsByRole, opByRole,
        result, win, lose, match: r["Match Win"], raw: r.raw,
      };
    }).filter((x) => x.Date && x.competitor);

    return parsedGames;
}

function buildTierDataFromGames(parsedGames) {
  const heroMap = {};
  parsedGames.forEach((g) => {
    const add = (name, type, w, l) => {
      const n = String(name || "").trim();
      if (!n) return;
      if (!heroMap[n]) heroMap[n] = { win: 0, lose: 0, type };
      heroMap[n].win += w;
      heroMap[n].lose += l;
    };
    (g.fsPick || []).forEach((h) => add(h, "1-hero", g.win, g.lose));
    add(g.competitor, "team", g.win, g.lose);
  });

  return Object.entries(heroMap)
    .map(([name, v]) => {
      const total = v.win + v.lose;
      return {
        name, win: v.win, lose: v.lose, type: v.type, total,
        winrate: total > 0 ? ((v.win / total) * 100).toFixed(1) : "0.0",
      };
    })
    .sort((a, b) => Number(b.winrate) - Number(a.winrate) || b.total - a.total);
}

/* log ทั้งซีซั่นจากชีท All_Scrim (คอลัมน์เดียวกับชีทสถิติ แต่ยาวกว่ามาก)
   ใช้กรอบแถวเดียวกับหน้า Home/Team Performance เพื่อไม่ให้บล็อกอาร์ไคฟ์ซีซั่นเก่าปนมา */
export function parseFullScrimLogFromWorkbook(wb) {
  const sheetName = pickHomeScrimSheetName(wb);
  if (!sheetName || !wb?.Sheets?.[sheetName]) return { sheetName: "", games: [] };
  const rawRows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
  const rows = constrainRowsToVisibleAllScrim(rawRows, sheetName);
  return { sheetName, games: buildGamesFromStatRows(rows) };
}

export async function parseFullScrimLogP(input) {
  const wb = await _toWorkbook(input);
  return parseFullScrimLogFromWorkbook(wb);
}

/* ====================== Specter_Scrim — the scouted log ======================
   All_Scrim is every game FS played. Specter_Scrim is the other thing a coach
   collects: other teams' scrims, watched and written down. No FS row in it at
   all, so it answers "what does this team do when we are not the opponent".

   Team A / Team B rather than FS / OP, and "Match Win" names the winning team,
   so the rows are parsed neutrally here and turned into one side's games where
   they are used (see specterGamesForTeam). */

const SPECTER_SHEET_NAMES = ["Specter_Scrim", "Specter Scrim", "specter_scrim"];

function pickSpecterSheetName(wb) {
  const names = wb?.SheetNames || [];
  const wanted = SPECTER_SHEET_NAMES.map((n) => n.toLowerCase());
  return names.find((n) => wanted.includes(String(n).trim().toLowerCase())) || "";
}

/** Column index by exact header text, tolerating the sheet's stray spaces. */
function specterCols(header = []) {
  const at = (label) =>
    header.findIndex((h) => String(h ?? "").trim().toLowerCase() === label.toLowerCase());
  const series = (prefix, n) =>
    Array.from({ length: n }, (_, i) => at(`${prefix} ${i + 1}`));
  const roleCols = (prefix) => ({
    DSL: at(`${prefix} DSL`),
    JUG: at(`${prefix} Jungle`),
    MID: at(`${prefix} Mid`),
    ADL: at(`${prefix} ADL`),
    SUP: at(`${prefix} Roaming`),
  });

  return {
    date: at("Date"),
    week: at("Week"),
    game: at("Game"),
    teamA: at("Team A"),
    teamB: at("Team B"),
    win: at("Match Win"),
    type: at("Type"),
    aBan: series("A Ban", 4),
    bBan: series("B Ban", 4),
    aPick: series("A Pick", 5),
    aPos: series("A Position", 5),
    bPick: series("B Pick", 5),
    bPos: series("B Position", 5),
    aRole: roleCols("A"),
    bRole: roleCols("B"),
  };
}

/**
 * @returns {{ sheetName: string, games: object[] }} neutral scouted rows:
 *   { Date, dateStr, week, type, teamA, teamB, aByRole, bByRole, winner }
 */
export function parseSpecterScrimFromWorkbook(wb) {
  const sheetName = pickSpecterSheetName(wb);
  if (!sheetName || !wb?.Sheets?.[sheetName]) return { sheetName: "", games: [] };

  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: "" });
  const idx = specterCols(rows[0] || []);
  if (idx.teamA < 0 || idx.teamB < 0) return { sheetName, games: [] };

  const pick = (row, cols) => cols.map((c) => (c >= 0 ? row[c] : ""));
  const games = [];

  for (const row of rows.slice(1)) {
    const teamA = String(row[idx.teamA] ?? "").trim();
    const teamB = String(row[idx.teamB] ?? "").trim();
    if (!teamA || !teamB) continue;

    const aByRole = buildRolePickMap({
      row,
      roleCols: idx.aRole,
      picks: pick(row, idx.aPick),
      positions: pick(row, idx.aPos),
    });
    const bByRole = buildRolePickMap({
      row,
      roleCols: idx.bRole,
      picks: pick(row, idx.bPick),
      positions: pick(row, idx.bPos),
    });
    // A row with no picks on either side is a spacer, not a game.
    if (!SCRIM_ROLE_ORDER.some((r) => aByRole[r] || bByRole[r])) continue;

    const D = parseDate(row[idx.date]);
    games.push({
      Date: D,
      dateStr: formatDateShort(D),
      week: idx.week >= 0 ? normalizeWeekValue(row[idx.week]) : "",
      game: idx.game >= 0 ? String(row[idx.game] ?? "").trim() : "",
      type: (idx.type >= 0 ? String(row[idx.type] ?? "").trim() : "") || "Scrim",
      teamA,
      teamB,
      aByRole,
      bByRole,
      aBans: pick(row, idx.aBan).map((v) => cleanHero(v)),
      bBans: pick(row, idx.bBan).map((v) => cleanHero(v)),
      winner: idx.win >= 0 ? String(row[idx.win] ?? "").trim() : "",
    });
  }

  return { sheetName, games };
}

export async function parseSpecterScrimP(input) {
  const wb = await _toWorkbook(input);
  return parseSpecterScrimFromWorkbook(wb);
}

/* ====================== parseComboStatsFromExcel (หัวตารางจริง + fallback) ====================== */
function _norm(s) { return String(s ?? "").trim().replace(/\s+/g, " ").toLowerCase(); }
function _num(x) {
  if (x === null || x === undefined || x === "") return 0;
  const s = String(x).trim();
  if (s.endsWith("%")) return parseFloat(s.replace(/[, ]/g, "").replace("%", "")) / 100;
  return parseFloat(s.replace(/[, ]/g, "")) || 0;
}
function _findHeaderRowFuzzy(sheetArr, leftLabels) {
  for (let r = 0; r < sheetArr.length; r++) {
    const row = (sheetArr[r] || []).map(_norm);
    const line = row.join("|");
    const ok = leftLabels.every(choices => choices.some(lbl => line.includes(lbl)));
    if (ok) return r;
  }
  return -1;
}
function _findHeaderColsFuzzy(row, labelGroups) {
  const res = {};
  const R = (row || []).map(_norm);
  for (const [key, choices] of Object.entries(labelGroups)) {
    let idx = -1;
    for (let c = 0; c < R.length; c++) {
      const cell = R[c];
      if (choices.some(ch => cell.includes(ch))) { idx = c; break; }
    }
    res[key] = idx;
  }
  return res;
}
function _push2(list, h1, h2, total, win, label) {
  total = Math.max(0, Math.round(_num(total)));
  let w = _num(win);
  // เผื่อกรณีคอลัมน์นั้นเป็น win rate (0.5) ไม่ใช่จำนวนชนะ — แต่ต้องเป็นเศษส่วนจริงๆ
  // "1" คือชนะ 1 เกม ถ้าเหมาว่าเป็น 100% จะกลายเป็นชนะทุกเกม (2 เกม 1 ชนะ → 2 ชนะ)
  if (w > 0 && w < 1 && total) w = Math.round(w * total);
  w = Math.max(0, Math.min(total, Math.round(w)));
  const losses = Math.max(0, total - w);
  const H1 = String(h1 || "").trim();
  const H2 = String(h2 || "").trim();
  if (_norm(H1) && _norm(H2) && total > 0) {
    list.push({ combo: `${H1} + ${H2}`, win: w, total, losses, comboType: label });
  }
}

function _extractCountsFromRow(row) {
  const ints = [];
  for (const v of (row || [])) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    if (/%$/.test(s)) continue; // ข้ามเปอร์เซ็นต์
    const n = Number(s.replace(/[, ]/g, ""));
    if (Number.isFinite(n) && n >= 0 && Math.floor(n) === n) ints.push(n);
  }
  if (ints.length < 2) return null;
  const a = ints[0], b = ints[1];
  if (a >= b) return { total: a, win: b }; // (Total, Win)
  return { total: a + b, win: a }; // (Win, Loss)
}

/* =========================================================
   parseComboStatsFromExcel (COMBO เดิม)
   ========================================================= */
export function parseComboStatsFromExcel(file, setComboStats, setDropdowns) {
  const reader = new FileReader();
  reader.onerror = () => {
    console.warn("อ่านไฟล์ไม่สำเร็จ");
    setComboStats({ combo2: [], combo2MidRoam: [], combo2DslJug: [], combo3: [] });
    if (setDropdowns) setDropdowns({ dates: [] });
  };
  reader.onload = e => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });
    const sheetName = wb.SheetNames.find(n => (n || "").toLowerCase().includes("combo"));
    if (!sheetName) {
      console.warn("ไม่พบชีท ComboStats");
      setComboStats({ combo2: [], combo2MidRoam: [], combo2DslJug: [], combo3: [] });
      if (setDropdowns) setDropdowns({ dates: [] });
      return;
    }
    const ws = wb.Sheets[sheetName];
    const sheetArr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

    const combo2MidRoam = [];
    const combo2DslJug = [];

    const mrHeaderRow =
      _findHeaderRowFuzzy(sheetArr, [["fs mid", " mid"], ["fs roaming", " roaming", " roam"]]);
    if (mrHeaderRow !== -1) {
      const cols = _findHeaderColsFuzzy(sheetArr[mrHeaderRow], {
        mid: ["fs mid", " mid"],
        roam: ["fs roaming", " roaming", " roam"],
        total: ["counta of fs", "count of fs", "games", "total"],
        win: ["sum of fs win", "wins", "win"],
        wr: ["win rate", "rate"]
      });
      let lastMid = "", lastRoam = "";
      for (let r = mrHeaderRow + 1; r < sheetArr.length; r++) {
        const row = sheetArr[r] || [];
        const rowJoin = (row.map(_norm).join("|"));
        if (rowJoin.includes("grand total")) break;

        let h1 = row[cols.mid], h2 = row[cols.roam];
        if (!h1) h1 = lastMid;
        if (!h2) h2 = lastRoam;
        if (!h1 && !h2) continue;

        const counts = _extractCountsFromRow(row);
        if (counts && counts.total > 0) {
          _push2(combo2MidRoam, h1, h2, counts.total, counts.win, "2-heroes (Mid+Roaming)");
        }
        lastMid = String(h1 || lastMid);
        lastRoam = String(h2 || lastRoam);
      }
    }

    const djHeaderRow =
      _findHeaderRowFuzzy(sheetArr, [["fs dsl", " dsl"], ["fs jungle", " jungle"]]);
    if (djHeaderRow !== -1) {
      const cols = _findHeaderColsFuzzy(sheetArr[djHeaderRow], {
        dsl: ["fs dsl", " dsl"],
        jungle: ["fs jungle", " jungle"],
        total: ["counta of fs", "count of fs", "games", "total"],
        win: ["sum of fs win", "wins", "win"],
        wr: ["win rate", "rate"]
      });
      let lastDSL = "", lastJungle = "";
      for (let r = djHeaderRow + 1; r < sheetArr.length; r++) {
        const row = sheetArr[r] || [];
        const rowJoin = (row.map(_norm).join("|"));
        if (rowJoin.includes("grand total")) break;

        let h1 = row[cols.dsl], h2 = row[cols.jungle];
        if (!h1) h1 = lastDSL;
        if (!h2) h2 = lastJungle;
        if (!h1 && !h2) continue;

        const counts = _extractCountsFromRow(row);
        if (counts && counts.total > 0) {
          _push2(combo2DslJug, h1, h2, counts.total, counts.win, "2-heroes (DSL+Jungle)");
        }
        lastDSL = String(h1 || lastDSL);
        lastJungle = String(h2 || lastJungle);
      }
    }

    const useFallback2 = (combo2MidRoam.length === 0 && combo2DslJug.length === 0);
    let combo3 = [];

    if (useFallback2) {
      // console.warn("[ComboStats] ใช้ fallback index-based");
      // Fallback: search for data blocks deeper in the sheet (often used in FS templates)
      for (let i = 1; i < sheetArr.length; i++) {
        const row = sheetArr[i] || [];
        // Detect dates to find block start
        const dateCol = findDateColInRange(row, 0, 50);
        if (dateCol === -1) continue;

        // Try Pair 1
        const [hCol1, hCol2] = pickHeroPair(row, dateCol + 2, dateCol);
        if (hCol1 !== -1 && hCol2 !== -1) {
            const h1 = row[hCol1], h2 = row[hCol2];
             if (isHeroish(h1) && isHeroish(h2)) {
                 const counts = _extractCountsFromRow(row);
                 if (counts && counts.total > 0) {
                     // Heuristic to decide type based on column index or adjacent cells
                     // Defaulting to mixed for fallback
                     _push2(combo2MidRoam, h1, h2, counts.total, counts.win, "2-heroes");
                 }
             }
        }
      }
    }

    // Try finding Triple Combos via fallback
    combo3 = [];
    for (let i = 1; i < sheetArr.length; i++) {
        const row = sheetArr[i] || [];
        const dateCol = findDateColInRange(row, 0, 50);
        if (dateCol === -1) continue;

        const [h1c, h2c, h3c] = pickHeroTriple(row, dateCol + 2, dateCol);
        const h1 = row[h1c], h2 = row[h2c], h3 = row[h3c];
        if (isHeroish(h1) && isHeroish(h2) && isHeroish(h3)) {
            const counts = _extractCountsFromRow(row);
            if (counts && counts.total > 0) {
                combo3.push({
                    combo: `${String(h1).trim()} + ${String(h2).trim()} + ${String(h3).trim()}`,
                    win: Math.max(0, Math.min(counts.total, counts.win)),
                    total: counts.total,
                    losses: Math.max(0, counts.total - Math.max(0, Math.min(counts.total, counts.win)))
                });
            }
        }
    }


    setComboStats({
      combo2: [...combo2MidRoam, ...combo2DslJug],
      combo2MidRoam,
      combo2DslJug,
      combo3
    });

    if (setDropdowns) {
      const dates = new Set();
      sheetArr.slice(1).forEach(r => {
        (r || []).forEach(cell => {
             const d = parseDate(cell);
             if (d) dates.add(d);
        });
      });
      setDropdowns({ dates: [...dates].sort() });
    }
  };
  reader.readAsArrayBuffer(file);
}

/* =========================================================
   parseFiltersFromAllScrimStats
   ========================================================= */
export function parseFiltersFromAllScrimStats(file, setFilterOptions) {
  const reader = new FileReader();
  reader.onerror = () => { console.warn("อ่านไฟล์ไม่สำเร็จ"); setFilterOptions({ dates: [], teams: [] }); };
  reader.onload = e => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });
    const sheetName = pickStatSheetName(wb);
    if (!sheetName) { console.warn("ไม่พบชีทสถิติ"); setFilterOptions({ dates: [], teams: [] }); return; }
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

    const idx = buildScrimIndexMap(rows);
    const dates = [], teams = [];
    for (let i = 1; i < rows.length; i++) {
      const d = parseDate(idx.date >= 0 ? rows[i][idx.date] : "");
      const t = idx.competitor >= 0 ? rows[i][idx.competitor] : "";
      if (d) dates.push(d);
      if (t) teams.push(String(t).trim());
    }
    setFilterOptions({
      dates: [...new Set(dates)].sort(),
      teams: [...new Set(teams)].sort()
    });
  };
  reader.readAsArrayBuffer(file);
}

/* =========================================================
   Parse RPL_Ban_Pick (meta hero stats)
   ========================================================= */
export function parseRplBanPickFromExcel(file, setParsed) {
  const reader = new FileReader();
  reader.onerror = () => {
    console.warn("อ่านไฟล์ไม่สำเร็จ");
    if (setParsed) setParsed({ heroStats: {}, totalGames: 0 });
  };
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });
    const sheetName = wb.SheetNames.find((n) => {
      const key = norm(n);
      return key === "rpl_ban_pick" || key === "fs_ban_pick" || key === "apl_ban_pick";
    });
    if (!sheetName) {
      console.warn("ไม่พบชีท Ban Pick");
      if (setParsed) setParsed({ heroStats: {}, totalGames: 0 });
      return;
    }

    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!rows.length) {
      if (setParsed) setParsed({ heroStats: {}, totalGames: 0 });
      return;
    }

    const idx = buildBanPickIndexMap(rows);
    const heroStats = {};

    const touch = (hero, kind, isWin = false) => {
      const h = cleanHero(hero);
      if (!h) return;
      if (!heroStats[h]) heroStats[h] = { pick: 0, ban: 0, win: 0, lose: 0, winRate: 0 };
      if (kind === "pick") {
        heroStats[h].pick += 1;
        if (isWin) heroStats[h].win += 1;
        else heroStats[h].lose += 1;
      } else if (kind === "ban") {
        heroStats[h].ban += 1;
      }
    };

    let totalGames = 0;
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i] || [];

      const fsPickArr = (
        (idx.fsPick || []).some((c) => c >= 0)
          ? (idx.fsPick || []).map((c) => (c >= 0 ? r[c] : ""))
          : SCRIM_ROLE_ORDER.map((role) => {
              const c = idx.fsRoleCols?.[role] ?? -1;
              return c >= 0 ? r[c] : "";
            })
      ).map((v) => cleanHero(v)).filter(Boolean);

      const fsBanArr = (idx.fsBan || [])
        .map((c) => (c >= 0 ? r[c] : ""))
        .map((v) => cleanHero(v))
        .filter(Boolean);

      const opPickArr = (
        (idx.opPick || []).some((c) => c >= 0)
          ? (idx.opPick || []).map((c) => (c >= 0 ? r[c] : ""))
          : SCRIM_ROLE_ORDER.map((role) => {
              const c = idx.opRoleCols?.[role] ?? -1;
              return c >= 0 ? r[c] : "";
            })
      ).map((v) => cleanHero(v)).filter(Boolean);

      const opBanArr = (idx.opBan || [])
        .map((c) => (c >= 0 ? r[c] : ""))
        .map((v) => cleanHero(v))
        .filter(Boolean);

      if (fsPickArr.length === 0 && fsBanArr.length === 0 && opPickArr.length === 0 && opBanArr.length === 0) continue;

      totalGames += 1;
      const { win } = resolveWinLoss(
        idx.matchWin >= 0 ? r[idx.matchWin] : "",
        idx.fsWin >= 0 ? r[idx.fsWin] : ""
      );
      const fsIsWin = win === 1;

      fsPickArr.forEach((h) => touch(h, "pick", fsIsWin));
      fsBanArr.forEach((h) => touch(h, "ban"));
      opPickArr.forEach((h) => touch(h, "pick", !fsIsWin));
      opBanArr.forEach((h) => touch(h, "ban"));
    }

    Object.values(heroStats).forEach((s) => {
      const totalPickGames = (s.win || 0) + (s.lose || 0);
      s.winRate = totalPickGames > 0 ? (s.win / totalPickGames) * 100 : 0;
    });

    if (setParsed) setParsed({ heroStats, totalGames });
  };

  reader.readAsArrayBuffer(file);
}


/* =========================================================
   (UPDATED) Dynamic Competitor Hero Stats
   ========================================================= */

const _A1 = (ws, range) =>
  XLSX.utils.sheet_to_json(ws, { header: 1, range, blankrows: false, defval: "" });

const _isHero = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return false;
  if (/^\d+([.,]\d+)?%?$/.test(s)) return false;
  if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(s)) return false;
  if (/^\d{8}$/.test(s)) return false;
  return true;
};

// Generic parser for One Hero and Combos that searches headers dynamically
function parseDynamicCompetitorStats(ws) {
  const sheetArr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const oneHero = [];
  const combo2 = [];
  const combo3 = [];

  // Define headers to look for (Regex based on Summer 2026 file)
  const posHeaders = [
    { key: "DSL", regex: /^(op|opp|enemy)\s*dsl/i },
    { key: "JUG", regex: /^(op|opp|enemy)\s*(jung|jug)/i },
    { key: "MID", regex: /^(op|opp|enemy)\s*mid/i },
    { key: "ADL", regex: /^(op|opp|enemy)\s*adl/i },
    { key: "SUP", regex: /^(op|opp|enemy)\s*(sup|roam)/i },
  ];

  // Scan top 10 rows for headers
  for (let r = 0; r < Math.min(sheetArr.length, 20); r++) {
      const row = sheetArr[r] || [];
      
      // Check for One Hero Stats Columns
      posHeaders.forEach(({key, regex}) => {
          const colIdx = row.findIndex(cell => regex.test(String(cell || "")));
          if (colIdx !== -1) {
              // Read down from this column until blank or "Grand Total"
              for (let i = r + 1; i < sheetArr.length; i++) {
                  const dataRow = sheetArr[i];
                  if (!dataRow) break;
                  const heroName = dataRow[colIdx];
                  if (!heroName || String(heroName).toLowerCase().includes("total")) break;
                  
                  // Look for stats in adjacent columns (Total, Win, etc.)
                  // Usually Total is in colIdx + 1, Win in colIdx + 2
                  const slice = dataRow.slice(colIdx + 1, colIdx + 10);
                  const counts = _extractCountsFromRow(slice);
                  
                  if (counts && counts.total > 0 && _isHero(heroName)) {
                      oneHero.push({
                          hero: String(heroName).trim(),
                          position: key,
                          total: counts.total,
                          win: Math.max(0, Math.min(counts.total, counts.win)),
                          winrate: counts.total ? +((Math.min(counts.total, counts.win)*100)/counts.total).toFixed(1) : 0
                      });
                  }
              }
          }
      });
  }

  // Deduplicate One Hero
  const uniqueOne = [];
  const mapOne = new Map();
  oneHero.forEach(o => {
      const k = `${o.hero}|${o.position}`;
      if (!mapOne.has(k)) { mapOne.set(k, true); uniqueOne.push(o); }
  });

  return { oneHero: uniqueOne.sort((a,b)=>b.total-a.total), combo2, combo3 };
}

function _roleFromText(s) {
  const t = norm(s);
  if (t.includes("dsl")) return "DSL";
  if (t.includes("jug") || t.includes("jung")) return "JUG";
  if (t.includes("mid")) return "MID";
  if (t.includes("adl")) return "ADL";
  if (t.includes("sup") || t.includes("roam")) return "SUP";
  return "";
}

function _looksLikeCountHeader(s) {
  return norm(s).includes("counta of") || norm(s).includes("count of");
}
function _looksLikeWinHeader(s) {
  return norm(s).includes("sum of") && norm(s).includes("win");
}

function _readNum(v) {
  const n = toNum(v);
  return Number.isFinite(n) ? n : null;
}

function parseTeamSheetSummaryStats(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (!rows.length) return { oneHero: [], combo2: [], combo3: [] };

  const oneHero = [];
  const combo2 = [];
  const combo3 = [];
  const usedBlocks = new Set();

  const pushOne = (hero, position, total, win, avgKda, avgParticipation, avgGpm, avgDmgPerMin, pickRate) => {
    const h = cleanHero(hero);
    const t = _readNum(total);
    const w = _readNum(win);
    if (!h || !position || !t || t <= 0) return;
    const ww = Math.max(0, Math.min(Math.round(t), Math.round(w ?? 0)));
    oneHero.push({
      hero: h,
      position,
      total: Math.round(t),
      win: ww,
      winrate: +((ww / Math.round(t)) * 100).toFixed(1),
      avgKda: _numOrNull(avgKda),
      avgParticipation: _normalizePercentOrNull(avgParticipation),
      avgGpm: _numOrNull(avgGpm),
      avgDmgPerMin: _numOrNull(avgDmgPerMin),
      pickRate: _normalizePercentOrNull(pickRate),
    });
  };

  const push2 = (h1, h2, total, win, comboType = "Opp real: dynamic 2") => {
    const a = cleanHero(h1), b = cleanHero(h2);
    const t = _readNum(total);
    const w = _readNum(win);
    if (!a || !b || !t || t <= 0) return;
    const ww = Math.max(0, Math.min(Math.round(t), Math.round(w ?? 0)));
    combo2.push({
      heroes: [a, b],
      combo: `${a} + ${b}`,
      total: Math.round(t),
      win: ww,
      comboType,
    });
  };

  const push3 = (h1, h2, h3, total, win, comboType = "Opp real: dynamic 3") => {
    const a = cleanHero(h1), b = cleanHero(h2), c = cleanHero(h3);
    const t = _readNum(total);
    const w = _readNum(win);
    if (!a || !b || !c || !t || t <= 0) return;
    const ww = Math.max(0, Math.min(Math.round(t), Math.round(w ?? 0)));
    combo3.push({
      heroes: [a, b, c],
      combo: `${a} + ${b} + ${c}`,
      total: Math.round(t),
      win: ww,
      comboType,
    });
  };

  const parseDown = (r0, cStart, type, roleA = "", roleB = "", roleC = "") => {
    const key = `${type}|${r0}|${cStart}`;
    if (usedBlocks.has(key)) return;
    usedBlocks.add(key);

    let emptyRun = 0;
    for (let r = r0 + 1; r < rows.length; r++) {
      const row = rows[r] || [];
      const a = row[cStart];
      const b = row[cStart + 1];
      const c = row[cStart + 2];
      const d = row[cStart + 3];
      const joined = `${a} ${b} ${c}`.toLowerCase();
      if (joined.includes("grand total")) break;

      if (type === "one") {
        if (!cleanHero(a)) {
          emptyRun += 1;
          if (emptyRun >= 10) break;
          continue;
        }
        emptyRun = 0;
        pushOne(a, roleA, b, c, row[cStart + 4], row[cStart + 5], row[cStart + 6], row[cStart + 7], row[cStart + 8]);
      } else if (type === "combo2") {
        if (!cleanHero(a) || !cleanHero(b)) {
          emptyRun += 1;
          if (emptyRun >= 10) break;
          continue;
        }
        emptyRun = 0;
        const label2 = `2-heroes (${roleA}+${roleB})`;
        push2(a, b, c, d, label2);
      } else if (type === "combo3") {
        if (!cleanHero(a) || !cleanHero(b) || !cleanHero(c)) {
          emptyRun += 1;
          if (emptyRun >= 10) break;
          continue;
        }
        emptyRun = 0;
        const label3 = `3-heroes (${roleA}+${roleB}+${roleC})`;
        push3(a, b, c, d, row[cStart + 4], label3);
      }
    }
  };

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r] || [];
    for (let c = 0; c < row.length; c++) {
      const h0 = row[c], h1 = row[c + 1], h2 = row[c + 2], h3 = row[c + 3], h4 = row[c + 4];

      // one-hero block
      if (_roleFromText(h0) && _looksLikeCountHeader(h1) && _looksLikeWinHeader(h2)) {
        parseDown(r, c, "one", _roleFromText(h0));
      }
      // combo2 block
      if (_roleFromText(h0) && _roleFromText(h1) && _looksLikeCountHeader(h2) && _looksLikeWinHeader(h3)) {
        parseDown(r, c, "combo2", _roleFromText(h0), _roleFromText(h1));
      }
      // combo3 block
      if (_roleFromText(h0) && _roleFromText(h1) && _roleFromText(h2) && _looksLikeCountHeader(h3) && _looksLikeWinHeader(h4)) {
        parseDown(r, c, "combo3", _roleFromText(h0), _roleFromText(h1), _roleFromText(h2));
      }
    }
  }

  const uniq = (arr, keyFn) => {
    const m = new Map();
    for (const x of arr) {
      const k = keyFn(x);
      const cur = m.get(k);
      if (!cur || (x.total || 0) > (cur.total || 0)) m.set(k, x);
    }
    return [...m.values()];
  };

  return {
    oneHero: uniq(oneHero, (x) => `${x.hero}|${x.position}`).sort((a, b) => (b.total - a.total)),
    combo2: uniq(combo2, (x) => x.combo).sort((a, b) => (b.total - a.total)),
    combo3: uniq(combo3, (x) => x.combo).sort((a, b) => (b.total - a.total)),
  };
}

/* =========================================================
   Main Parser functions
   ========================================================= */

export function parseComboStatsSheet(sheet, sheetName, teamName) {
  const sName = String(sheetName || "").trim();
  const key = String(teamName || "").trim().toUpperCase();

  // 0) Team sheet summary blocks (one-hero + combo2 + combo3)
  const teamSummary = parseTeamSheetSummaryStats(sheet);
  const hasTeamSummary =
    (teamSummary.oneHero?.length || 0) +
    (teamSummary.combo2?.length || 0) +
    (teamSummary.combo3?.length || 0) > 0;
  if (hasTeamSummary) {
    return { sheetName: key, ...teamSummary };
  }

  // 1. Try Dynamic Parse for Competitor Stats or Team Specific Sheets
  if ((/^competitor_hero_stats$/i.test(sName)) || ONE_HERO_MAP[key]) {
      const dynamicRes = parseDynamicCompetitorStats(sheet);
      if (dynamicRes.oneHero.length > 0) {
          return { sheetName: key, ...dynamicRes };
      }
  }

  // 2. Fallback to Legacy Logic if Dynamic fails
  const blocks = ONE_HERO_MAP[key] || [];
  const oneHero = blocks
    .flatMap(cfg => parseBlock3(sheet, cfg))
    .sort((a, b) => (b.total - a.total) || (b.win - a.win));
    
  return { sheetName: key, oneHero, combo2: [], combo3: [] };
}

export function parseTeamCombosFromExcel(file, setTeamCombos, sheets = SUMMER_2026_TEAMS) {
  const reader = new FileReader();
  reader.onerror = () => {
    console.warn("อ่านไฟล์ไม่สำเร็จ");
    if (setTeamCombos) setTeamCombos({ combo2: [], combo3: [], byTeam: {} });
  };
  reader.onload = e => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: "array" });

    const byTeam = {};
    const all2 = [];
    const all3 = [];

    (sheets || []).forEach(name => {
      const sheet = wb.Sheets[name];
      if (!sheet) return;
      const res = parseComboStatsSheet(sheet, name, name);
      byTeam[name] = { combo2: res.combo2, combo3: res.combo3, oneHero: res.oneHero };
      all2.push(...res.combo2.map(o => ({ ...o, team: name })));
      all3.push(...res.combo3.map(o => ({ ...o, team: name })));
    });

    if (setTeamCombos) setTeamCombos({ combo2: all2, combo3: all3, byTeam });
  };
  reader.readAsArrayBuffer(file);
}


/* =========================================================
   Player-Role-Hero (pivot)
   ========================================================= */
// รายชื่อผู้เล่นของทีม ใส่เองตามโรสเตอร์ปัจจุบัน เช่น { MID: "ชื่อผู้เล่น", ... }
// ปล่อยว่างไว้ = ตาราง player-role-hero จะไม่มีข้อมูล (ตอนนี้ยังไม่มีหน้าไหนเรียกใช้)
export const FS_ROLE_TO_PLAYER = {};
export function buildPlayerHeroStatsByRole(games, roleToPlayer = FS_ROLE_TO_PLAYER) {
  const byPR = {};
  const add = (player, role, hero, winFlag) => {
    if (!player || !hero || !role) return;
    const p = String(player).trim(), r = String(role).trim().toUpperCase(), h = String(hero).trim();
    byPR[p] = byPR[p] || {};
    byPR[p][r] = byPR[p][r] || {};
    const slot = (byPR[p][r][h] = byPR[p][r][h] || { total: 0, win: 0, lose: 0 });
    slot.total += 1; winFlag ? slot.win++ : slot.lose++;
  };
  games.forEach(g => {
    const positions = g.fsPosition || [];
    const picks = g.fsPick || [];
    const isWin = g.result === "Win";
    for (let i = 0; i < 5; i++) {
      let role = (positions[i] || "").toUpperCase();
      if (role === "ROAMING" || role === "SUPPORT") role = "SUP";
      if (role === "MIDLANE") role = "MID";
      if (role === "JUNGLE") role = "JUG";
      const player = roleToPlayer[role];
      const hero = picks[i];
      if (player && hero) add(player, role, hero, isWin);
    }
  });

  const out = {};
  Object.entries(byPR).forEach(([p, byRole]) => {
    out[p] = {};
    Object.entries(byRole).forEach(([r, heroMap]) => {
      out[p][r] = Object.entries(heroMap).map(([h, v]) => ({
        player: p, role: r, hero: h,
        total: v.total, win: v.win, lose: v.lose,
        winrate: v.total ? +((v.win / v.total) * 100).toFixed(2) : 0
      })).sort((a, b) => b.winrate - a.winrate || b.total - a.total);
    });
  });
  return out;
}

/* =========================================================
   Promise versions
   ========================================================= */
export function parseScrimStatsFromExcelP(file) {
  return new Promise(resolve => parseScrimStatsFromExcel(file, (games) => resolve(games)));
}
export function parseGamesFromExcelP(file) {
  return new Promise(resolve => parseGamesFromExcel(file, (games) => resolve(games), null));
}
export function parseComboStatsFromExcelP(file) {
  return new Promise(resolve => parseComboStatsFromExcel(file, (res) => resolve(res), null));
}
export function parseFiltersFromAllScrimStatsP(file) {
  return new Promise(resolve => parseFiltersFromAllScrimStats(file, (opts) => resolve(opts)));
}
export function parseTeamCombosFromExcelP(file, sheets = SUMMER_2026_TEAMS) {
  return new Promise(resolve => parseTeamCombosFromExcel(file, (res) => resolve(res), sheets));
}
export function parseRplBanPickFromExcelP(file) {
  return new Promise(resolve => parseRplBanPickFromExcel(file, (res) => resolve(res)));
}

/* =========================================================
   Workbook adapter + รวมผล
   ========================================================= */
async function _toWorkbook(input) {
  if (input && typeof input === 'object' && Array.isArray(input.SheetNames) && input.Sheets) {
    return input;
  }
  if (typeof Blob !== "undefined" && input instanceof Blob) {
    const buf = await input.arrayBuffer();
    return XLSX.read(new Uint8Array(buf), { type: "array" });
  }
  if (input instanceof ArrayBuffer) {
    return XLSX.read(new Uint8Array(input), { type: "array" });
  }
  if (input && (input.buffer instanceof ArrayBuffer || input instanceof Uint8Array)) {
    const u8 = input instanceof Uint8Array ? input : new Uint8Array(input);
    return XLSX.read(u8, { type: "array" });
  }
  throw new Error("parseWorkbookToComboStats: Unsupported input type");
}

export async function parseWorkbookToComboStats(input, options = {}) {
  const { sheets = SUMMER_2026_TEAMS, includeOneHero = true } = options;
  const wb = await _toWorkbook(input);

  const byTeam = {};
  const all2 = [];
  const all3 = [];
  const allOneHero = [];

  (sheets || []).forEach(name => {
    const sheet = wb.Sheets[name];
    if (!sheet) return;
    const res = parseComboStatsSheet(sheet, name, name);
    byTeam[name] = {
      combo2: res.combo2 || [],
      combo3: res.combo3 || [],
      oneHero: res.oneHero || []
    };
    (res.combo2 || []).forEach(o => all2.push({ ...o, team: name }));
    (res.combo3 || []).forEach(o => all3.push({ ...o, team: name }));
    if (includeOneHero) (res.oneHero || []).forEach(o => allOneHero.push({ ...o, team: name }));
  });

  return { combo2: all2, combo3: all3, oneHero: allOneHero, byTeam };
}

/* =========================================================
   (ใหม่) ใช้งานง่าย: ดึง “ตามทีมจริง” จาก Competitor_Hero_stats
   ========================================================= */
const TEAM_KEY = {
  BAC: "BAC", "Bacon Time": "BAC",
  PSG: "PSG", "PSG Esports": "PSG",
  BRU: "BRU", Buriram: "BRU", "Buriram United": "BRU",
  FW: "FW", HKA: "HKA", DCG: "DCG",
  ONE: "ONE", "ONE Esports": "ONE", "ONE Team": "ONE",
  FPT: "FPT", "FPT Esports": "FPT",
  SLX: "SLX", TEN: "TEN", GODJI: "GODJI", EA: "EA", KOG: "KOG", HD: "HD",
  TLN: "TEN",
  EAG: "EA",
  HDG: "HD"
};

export async function parseOpponentCombosForTeam(input, selectedLabel) {
  const key = TEAM_KEY[selectedLabel] || String(selectedLabel || "").trim().toUpperCase();
  const wb = await _toWorkbook(input);

  // 1) ใช้ชีททีมโดยตรงก่อน (มี summary ครบกว่าในหลายไฟล์)
  const wsTeam = wb.Sheets[key];
  if (wsTeam) {
      const resTeam = parseTeamSheetSummaryStats(wsTeam);
      const hasTeam =
        (resTeam.oneHero?.length || 0) +
        (resTeam.combo2?.length || 0) +
        (resTeam.combo3?.length || 0) > 0;
      if (hasTeam) return resTeam;

      const dynamicTeam = parseDynamicCompetitorStats(wsTeam);
      if ((dynamicTeam.oneHero?.length || 0) > 0) return dynamicTeam;
  }

  // 2) fallback ไปหน้า Competitor_Hero_stats
  const wsComp = wb.Sheets["Competitor_Hero_stats"];
  if (wsComp) {
      const res = parseDynamicCompetitorStats(wsComp);
      if (res.oneHero.length > 0) return res;
  }

  return { oneHero: [], combo2: [], combo3: [] };
}

/* =========================================================
   Exports & UI Helpers
   ========================================================= */
export const COMPETITOR_HERO_RANGES = {}; // Deprecated in favor of Dynamic Parser

export async function oppComboStats(input, team) {
  return parseOpponentCombosForTeam(input, team);
}

export function getCompetitorHeroStatsRanges(teamLabel) {
  return null; // Ranges are now dynamic
}

function __labelPairForUI(labelRaw) {
  const L = String(labelRaw || "").replace(/^Opp real:\s*/i, "").toUpperCase();
  if (L.includes("MID") && L.includes("SUP")) return "2-heroes (MID+SUP)";
  if (L.includes("ADL") && L.includes("SUP")) return "2-heroes (ADL+SUP)";
  if (L.includes("DSL") && (L.includes("JUG") || L.includes("JUNGLE"))) return "2-heroes (DSL+Jungle)";
  if ((L.includes("JUG") || L.includes("JUNGLE")) && L.includes("SUP")) return "2-heroes (JUG+SUP)";
  if ((L.includes("JUG") || L.includes("JUNGLE")) && L.includes("MID")) return "2-heroes (JUG+MID)";
  return "2-heroes";
}

export function normalizeOppToComboStats(opp) {
  // ฟังก์ชันนี้แปลง structure เพื่อให้ UI แสดงผลได้
  const to2 = (list) => (list || []).map(r => {
    const wins = Math.round(r.win || 0);
    const total = Math.round(r.total || 0);
    return {
      combo: (r.heroes && r.heroes.length ? r.heroes.join(" + ") : String(r.combo || "")),
      win: wins,
      total: total,
      losses: Math.max(0, total - wins),
      comboType: __labelPairForUI(r.comboType || "")
    };
  });

  const to3 = (list) => (list || []).map(r => {
    const wins = Math.round(r.win || 0);
    const total = Math.round(r.total || 0);
    const combo = (r.heroes && r.heroes.length ? r.heroes.join(" + ") : String(r.combo || ""));
    let comboType = "3-heroes";
    return { combo, win: wins, total, losses: Math.max(0, total - wins), comboType };
  });

  return {
    combo2: to2(opp?.combo2 || []),
    combo3: to3(opp?.combo3 || []),
    oneHero: (opp?.oneHero || []).map(o => ({
      hero: String(o.hero || "").trim(),
      position: String(o.position || "").toUpperCase(),
      total: Math.round(o.total || 0),
      win: Math.round(o.win || 0),
      winrate: o.total ? +(o.win * 100 / o.total).toFixed(1) : 0,
      avgKda: _numOrNull(o.avgKda),
      avgParticipation: _normalizePercentOrNull(o.avgParticipation),
      avgGpm: _numOrNull(o.avgGpm),
      avgDmgPerMin: _numOrNull(o.avgDmgPerMin),
      pickRate: _normalizePercentOrNull(o.pickRate),
    }))
  };
}

export async function makeComboStatsForPerspective(input, perspective = 'us', opponentLabel = '') {
  const p = String(perspective || 'us').toLowerCase();
  if (p === 'them') {
    const opp = await parseOpponentCombosForTeam(input, opponentLabel);
    return normalizeOppToComboStats(opp);
  }
  const us = await parseComboStatsFromExcelP(input);
  return us || { combo2: [], combo3: [] };
}

export async function makeBothComboStats(input, opponentLabel = '') {
  const [us, opp] = await Promise.all([
    parseComboStatsFromExcelP(input),
    parseOpponentCombosForTeam(input, opponentLabel)
  ]);
  return { us, them: normalizeOppToComboStats(opp) };
}

/* =========================================================
   RPL_Stats helpers (team filter list)
   ========================================================= */
function _isRealTeamValue(v) {
  const s = String(v ?? "").trim();
  if (!s) return false;
  const low = s.toLowerCase();
  if (low === "team" || low.includes("total")) return false;
  return true;
}

function _extractTeamsFromStatLikeSheet(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (!rows.length) return [];

  // Prefer the filtered table block (e.g. RPL_Stats!B377:O976)
  let headerRow = -1;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r] || [];
    const hasGame = row.some((c) => norm(c) === "game");
    const hasTeam = row.some((c) => norm(c) === "team");
    if (hasGame && hasTeam) {
      headerRow = r;
      break;
    }
  }
  if (headerRow === -1) return [];

  const header = rows[headerRow] || [];
  const teamCol = header.findIndex((c) => norm(c) === "team");
  if (teamCol < 0) return [];

  const teams = new Set();
  for (let r = headerRow + 1; r < rows.length; r++) {
    const t = rows[r]?.[teamCol];
    if (_isRealTeamValue(t)) teams.add(String(t).trim().toUpperCase());
  }

  return [...teams].sort();
}

function _extractTeamsFromScrimLikeSheet(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (!rows.length) return [];

  const idx = buildScrimIndexMap(rows);
  if (idx.competitor < 0) return [];

  const teams = new Set();
  for (let r = 1; r < rows.length; r++) {
    const t = rows[r]?.[idx.competitor];
    if (_isRealTeamValue(t)) teams.add(String(t).trim().toUpperCase());
  }
  return [...teams].sort();
}

export async function parseRplStatsTeams(input) {
  const wb = await _toWorkbook(input);
  const primaryStatSheet = pickStatSheetName(wb);
  if (primaryStatSheet && wb.Sheets[primaryStatSheet]) {
    const teams = _extractTeamsFromScrimLikeSheet(wb.Sheets[primaryStatSheet]);
    if (teams.length > 0) return teams;
  }

  const scrimCandidates = ["Girl_Scrim", "APL_Scrim", "All_Scrim", "All_Scrim_Stats", "RPL_Scrim"];
  for (const name of scrimCandidates) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const teams = _extractTeamsFromScrimLikeSheet(ws);
    if (teams.length > 0) return teams;
  }

  const candidates = ["APL_Stats", "APL", "RPL_Stats", "RPL_Stat", "RPL"];

  for (const name of candidates) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const teams = _extractTeamsFromStatLikeSheet(ws);
    if (teams.length > 0) return teams;
  }
  return [];
}

export function parseRplStatsTeamsP(file) {
  return parseRplStatsTeams(file);
}

/* =========================================================
   FS one-hero from RPL_player_stats
   ========================================================= */
const FS_PLAYER_BLOCKS = [
  { startCol: 0, position: "DSL" },   // A..I
  { startCol: 10, position: "JUG" },  // K..S
  { startCol: 20, position: "MID" },  // U..AC
  { startCol: 30, position: "ADL" },  // AE..AM
  { startCol: 40, position: "SUP" },  // AO..AW
];

function _numOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[% ,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function _normalizePercentOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = _numOrNull(v);
  if (!Number.isFinite(n)) return null;
  if (n >= 0 && n <= 1.0001) return +(n * 100).toFixed(2);
  if (n >= 0 && n <= 100) return +n.toFixed(2);
  return +n.toFixed(2);
}

function _cellByRC(ws, row1, col0) {
  const addr = XLSX.utils.encode_cell({ r: row1 - 1, c: col0 });
  return getCell(ws, addr);
}

function _extractFsOneHeroFromRplPlayerStatsSheet(ws) {
  const totalGames = _numOrNull(getCell(ws, "D1")) || 0;
  const out = [];

  for (const block of FS_PLAYER_BLOCKS) {
    let emptyRun = 0;
    for (let r = 4; r <= 320; r++) {
      const heroRaw = _cellByRC(ws, r, block.startCol);
      const hero = cleanHero(heroRaw);

      if (!hero) {
        emptyRun += 1;
        if (emptyRun >= 12) break;
        continue;
      }
      emptyRun = 0;

      const totalRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 1)); // B/L/V/AF/AP
      const winRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 2));   // C/M/W/AG/AQ
      const wrRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 3));    // D/N/X/AH/AR
      const avgKdaRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 4)); // E/O/Y/AI/AS
      const avgParticipationRaw = _normalizePercentOrNull(_cellByRC(ws, r, block.startCol + 5)); // F/P/Z/AJ/AT
      const avgGpmRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 6)); // G/Q/AA/AK/AU
      const avgDmgPerMinRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 7)); // H/R/AB/AL/AV
      const pickRateRaw = _numOrNull(_cellByRC(ws, r, block.startCol + 8)); // I/S/AC/AM/AW

      let total = totalRaw;
      if ((total === null || total <= 0) && totalGames > 0 && pickRateRaw !== null && pickRateRaw > 0) {
        // fallback กรณีไฟล์มีแต่ pick rate ที่คำนวณไว้
        total = Math.round(pickRateRaw * totalGames);
      }
      if (total === null || total <= 0) continue;

      let win = winRaw;
      if ((win === null || win < 0 || win > total) && wrRaw !== null) {
        const wr01 = wrRaw >= 0 && wrRaw <= 1.0001 ? wrRaw : (wrRaw > 1 && wrRaw <= 100 ? wrRaw / 100 : null);
        if (wr01 !== null) win = Math.round(total * wr01);
      }
      if (win === null || !Number.isFinite(win)) win = 0;
      win = Math.max(0, Math.min(total, Math.round(win)));

      out.push({
        hero: String(hero).trim(),
        position: block.position,
        total: Math.round(total),
        win,
        winrate: total > 0 ? +((win / total) * 100).toFixed(1) : 0,
        avgKda: Number.isFinite(avgKdaRaw) ? +avgKdaRaw.toFixed(2) : null,
        avgParticipation: avgParticipationRaw,
        avgGpm: Number.isFinite(avgGpmRaw) ? +avgGpmRaw.toFixed(2) : null,
        avgDmgPerMin: Number.isFinite(avgDmgPerMinRaw) ? +avgDmgPerMinRaw.toFixed(2) : null,
        pickRate: _normalizePercentOrNull(pickRateRaw),
      });
    }
  }

  const merged = new Map();
  for (const row of out) {
    const key = `${row.hero}|${row.position}`;
    const cur = merged.get(key);
    if (!cur) {
      merged.set(key, {
        ...row,
        _wKda: row.avgKda != null ? row.total : 0,
        _sumKda: row.avgKda != null ? row.avgKda * row.total : 0,
        _wPart: row.avgParticipation != null ? row.total : 0,
        _sumPart: row.avgParticipation != null ? row.avgParticipation * row.total : 0,
        _wGpm: row.avgGpm != null ? row.total : 0,
        _sumGpm: row.avgGpm != null ? row.avgGpm * row.total : 0,
        _wDmg: row.avgDmgPerMin != null ? row.total : 0,
        _sumDmg: row.avgDmgPerMin != null ? row.avgDmgPerMin * row.total : 0,
        _wPick: row.pickRate != null ? row.total : 0,
        _sumPick: row.pickRate != null ? row.pickRate * row.total : 0,
      });
    }
    else {
      cur.total += row.total;
      cur.win += row.win;
      cur.winrate = cur.total > 0 ? +((cur.win / cur.total) * 100).toFixed(1) : 0;
      if (row.avgKda != null) {
        cur._sumKda += row.avgKda * row.total;
        cur._wKda += row.total;
      }
      if (row.avgParticipation != null) {
        cur._sumPart += row.avgParticipation * row.total;
        cur._wPart += row.total;
      }
      if (row.avgGpm != null) {
        cur._sumGpm += row.avgGpm * row.total;
        cur._wGpm += row.total;
      }
      if (row.avgDmgPerMin != null) {
        cur._sumDmg += row.avgDmgPerMin * row.total;
        cur._wDmg += row.total;
      }
      if (row.pickRate != null) {
        cur._sumPick += row.pickRate * row.total;
        cur._wPick += row.total;
      }
    }
  }

  return [...merged.values()]
    .map((x) => ({
      hero: x.hero,
      position: x.position,
      total: x.total,
      win: x.win,
      winrate: x.winrate,
      avgKda: x._wKda > 0 ? +(x._sumKda / x._wKda).toFixed(2) : null,
      avgParticipation: x._wPart > 0 ? +(x._sumPart / x._wPart).toFixed(2) : null,
      avgGpm: x._wGpm > 0 ? +(x._sumGpm / x._wGpm).toFixed(2) : null,
      avgDmgPerMin: x._wDmg > 0 ? +(x._sumDmg / x._wDmg).toFixed(2) : null,
      pickRate: x._wPick > 0 ? +(x._sumPick / x._wPick).toFixed(2) : null,
    }))
    .sort((a, b) => (b.total - a.total) || (b.win - a.win));
}

export async function parseFsOneHeroFromRplPlayerStats(input) {
  const wb = await _toWorkbook(input);
  const ws = wb.Sheets["RPL_player_stats"] || wb.Sheets["player_stats"] || wb.Sheets["Player_Stats"] || wb.Sheets["APL_player_stats"];
  if (!ws) return [];
  return _extractFsOneHeroFromRplPlayerStatsSheet(ws);
}

export function parseFsOneHeroFromRplPlayerStatsP(file) {
  return parseFsOneHeroFromRplPlayerStats(file);
}

/* =========================================================
   Early Game Macro: Objectives / Tower / Gold @ 4'/8'/12'/15'
   + First Slayer (จาก All_Scrim / All_Scrim_Stats)
   ========================================================= */
const EARLY_MACRO_TIME_MARKS = [4, 8, 12, 15];
const EARLY_MACRO_METRICS = ["dragon", "slayer", "tower", "gold"];

function _normHeader(v) {
  return String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function _avtToCode(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "fs") return "FS";
  if (s === "competitor" || s === "op" || s === "opponent") return "OP";
  if (s === "same" || s === "tie" || s === "draw") return "SAME";
  return null;
}

function _findEarlyMacroIndex(headerRow) {
  const idx = { date: -1, week: -1, competitor: -1, fsSide: -1, type: -1, fsWin: -1, firstBlood: -1, firstAbyssal: -1, marks: {} };
  for (let i = 0; i < headerRow.length; i++) {
    const h = _normHeader(headerRow[i]);
    if (!h) continue;
    if (idx.date < 0 && h === "date") idx.date = i;
    if (idx.week < 0 && h === "week") idx.week = i;
    if (idx.competitor < 0 && h === "competitor") idx.competitor = i;
    if (idx.fsSide < 0 && h === "fs side") idx.fsSide = i;
    if (idx.type < 0 && h === "type") idx.type = i;
    if (idx.fsWin < 0 && h === "fs win") idx.fsWin = i;
    if (idx.firstBlood < 0 && h === "first blood") idx.firstBlood = i;
    if (idx.firstAbyssal < 0 && h === "first abyssal") idx.firstAbyssal = i;

    for (const t of EARLY_MACRO_TIME_MARKS) {
      for (const m of EARLY_MACRO_METRICS) {
        const fs = `${t}' ${m}`;
        const op = `c ${t}' ${m}`;
        const avt = `avt ${t}' ${m}`;
        if (h === fs)        (idx.marks[t] ??= {})[`${m}_fs`]  = i;
        else if (h === op)   (idx.marks[t] ??= {})[`${m}_op`]  = i;
        else if (h === avt)  (idx.marks[t] ??= {})[`${m}_avt`] = i;
      }
    }
  }
  return idx;
}

function _numCell(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _firstSlayerWinner(marks) {
  for (const t of EARLY_MACRO_TIME_MARKS) {
    const fs = marks[t]?.slayer?.fs;
    const op = marks[t]?.slayer?.op;
    const hasAny = (fs != null && fs > 0) || (op != null && op > 0);
    if (!hasAny) continue;
    if ((fs || 0) > (op || 0)) return "FS";
    if ((op || 0) > (fs || 0)) return "OP";
    return marks[t]?.slayer?.avt || "SAME";
  }
  return null;
}

export async function parseEarlyGameMacro(input) {
  const wb = await _toWorkbook(input);
  const ws = wb.Sheets["All_Scrim"] || wb.Sheets["All_Scrim_Stats"];
  if (!ws) return [];

  const rawArr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const arr = constrainRowsToVisibleAllScrim(
    rawArr,
    wb.Sheets["All_Scrim"] ? "All_Scrim" : "All_Scrim_Stats"
  );
  if (!arr.length) return [];

  const idx = _findEarlyMacroIndex(arr[0] || []);
  if (idx.date < 0 || idx.competitor < 0) return [];

  const rows = [];
  for (let r = 1; r < arr.length; r++) {
    const row = arr[r] || [];
    const date = row[idx.date];
    const competitor = String(row[idx.competitor] ?? "").trim();
    if (!date || !competitor) continue;

    const marks = {};
    let anyMark = false;
    for (const t of EARLY_MACRO_TIME_MARKS) {
      const cols = idx.marks[t] || {};
      const slot = {};
      for (const m of EARLY_MACRO_METRICS) {
        const fs = _numCell(row[cols[`${m}_fs`]]);
        const op = _numCell(row[cols[`${m}_op`]]);
        const avt = _avtToCode(row[cols[`${m}_avt`]]);
        if (fs != null || op != null || avt != null) anyMark = true;
        slot[m] = { fs, op, avt };
      }
      marks[t] = slot;
    }
    if (!anyMark) continue;

    const firstTeamWinner = (rawCell) => {
      const s = String(rawCell ?? "").trim().toUpperCase();
      if (!s) return null;
      if (s === "FS") return "FS";
      // Legacy rows stored a number here (minute/count, not a team) — ignore
      // those instead of mis-attributing them to the opponent.
      if (/^\d+(\.\d+)?$/.test(s)) return null;
      // "same"/"tie" means neither side took it first.
      if (s === "SAME" || s === "TIE" || s === "DRAW" || s === "SAME ONE") return null;
      // Any other non-empty value is an opponent team code → opponent took it.
      return "OP";
    };
    rows.push({
      date: String(date),
      week: idx.week >= 0 ? row[idx.week] : "",
      competitor: competitor.toUpperCase(),
      fsSide: idx.fsSide >= 0 ? String(row[idx.fsSide] ?? "") : "",
      type: idx.type >= 0 ? String(row[idx.type] ?? "") : "",
      fsWin: idx.fsWin >= 0 ? (Number(row[idx.fsWin]) === 1 ? 1 : 0) : 0,
      marks,
      firstSlayer: _firstSlayerWinner(marks),
      firstBlood: idx.firstBlood >= 0 ? firstTeamWinner(row[idx.firstBlood]) : null,
      firstAbyssal: idx.firstAbyssal >= 0 ? firstTeamWinner(row[idx.firstAbyssal]) : null,
    });
  }
  return rows;
}

export function parseEarlyGameMacroP(input) {
  return parseEarlyGameMacro(input);
}

export function aggregateEarlyGameMacro(rows) {
  const games = rows.length;
  const empty = () => ({
    fsAvg: 0, opAvg: 0, delta: 0,
    fsLead: 0, tie: 0, opLead: 0, sampled: 0,
    fsLeadPct: 0, tiePct: 0, opLeadPct: 0,
    wrWhenFsLead: 0, wrWhenTie: 0, wrWhenOpLead: 0,
  });

  const byMark = {};
  for (const t of EARLY_MACRO_TIME_MARKS) {
    byMark[t] = {};
    for (const m of EARLY_MACRO_METRICS) byMark[t][m] = empty();
    byMark[t].objectives = empty();
  }

  if (!games) {
    const emptyFirst = { fs: 0, op: 0, none: 0, fsPct: 0, opPct: 0, nonePct: 0, wrWhenFs: 0, wrWhenOp: 0, wrFs: 0, wrOp: 0 };
    return {
      games,
      byMark,
      firstSlayer: { ...emptyFirst, same: 0, samePct: 0 },
      firstBlood: { ...emptyFirst },
      firstAbyssal: { ...emptyFirst },
      insights: [],
    };
  }

  for (const t of EARLY_MACRO_TIME_MARKS) {
    const sums = {};
    EARLY_MACRO_METRICS.forEach((m) => { sums[m] = { fs: 0, op: 0, fsN: 0, opN: 0, fsLead: 0, tie: 0, opLead: 0, wrFs: 0, wrTie: 0, wrOp: 0, sampled: 0 }; });
    const objSums = { fs: 0, op: 0, fsN: 0, opN: 0, fsLead: 0, tie: 0, opLead: 0, wrFs: 0, wrTie: 0, wrOp: 0, sampled: 0 };

    for (const g of rows) {
      const slot = g.marks?.[t] || {};
      let objFs = 0, objOp = 0, objHasNum = false;
      let objAvtVotes = { FS: 0, OP: 0, SAME: 0 };

      for (const m of EARLY_MACRO_METRICS) {
        const v = slot[m] || {};
        if (v.fs != null) { sums[m].fs += v.fs; sums[m].fsN += 1; }
        if (v.op != null) { sums[m].op += v.op; sums[m].opN += 1; }
        if (v.avt) {
          sums[m].sampled += 1;
          if (v.avt === "FS")   { sums[m].fsLead += 1; if (g.fsWin) sums[m].wrFs += 1; }
          if (v.avt === "OP")   { sums[m].opLead += 1; if (g.fsWin) sums[m].wrOp += 1; }
          if (v.avt === "SAME") { sums[m].tie    += 1; if (g.fsWin) sums[m].wrTie+= 1; }
        }
        if (m === "dragon" || m === "slayer") {
          if (v.fs != null || v.op != null) {
            objFs += v.fs || 0;
            objOp += v.op || 0;
            objHasNum = true;
          }
          if (v.avt) objAvtVotes[v.avt] = (objAvtVotes[v.avt] || 0) + 1;
        }
      }

      if (objHasNum) { objSums.fs += objFs; objSums.op += objOp; objSums.fsN += 1; objSums.opN += 1; }
      const objLead = objFs > objOp ? "FS" : objOp > objFs ? "OP" : objHasNum ? "SAME" : null;
      if (objLead) {
        objSums.sampled += 1;
        if (objLead === "FS")   { objSums.fsLead += 1; if (g.fsWin) objSums.wrFs += 1; }
        if (objLead === "OP")   { objSums.opLead += 1; if (g.fsWin) objSums.wrOp += 1; }
        if (objLead === "SAME") { objSums.tie    += 1; if (g.fsWin) objSums.wrTie+= 1; }
      }
    }

    const finalize = (s) => {
      const fsAvg = s.fsN ? s.fs / s.fsN : 0;
      const opAvg = s.opN ? s.op / s.opN : 0;
      const sampled = s.sampled;
      const pct = (n) => sampled ? (n / sampled) * 100 : 0;
      const wr = (wins, lead) => lead ? (wins / lead) * 100 : 0;
      return {
        fsAvg, opAvg, delta: fsAvg - opAvg, sampled,
        fsLead: s.fsLead, tie: s.tie, opLead: s.opLead,
        fsLeadPct: pct(s.fsLead), tiePct: pct(s.tie), opLeadPct: pct(s.opLead),
        wrWhenFsLead: wr(s.wrFs, s.fsLead),
        wrWhenTie:    wr(s.wrTie, s.tie),
        wrWhenOpLead: wr(s.wrOp,  s.opLead),
      };
    };

    EARLY_MACRO_METRICS.forEach((m) => { byMark[t][m] = finalize(sums[m]); });
    byMark[t].objectives = finalize(objSums);
  }

  const summarizeFirst = (key) => {
    const acc = { fs: 0, op: 0, none: 0, wrFs: 0, wrOp: 0 };
    for (const g of rows) {
      if (g[key] === "FS")      { acc.fs += 1; if (g.fsWin) acc.wrFs += 1; }
      else if (g[key] === "OP") { acc.op += 1; if (g.fsWin) acc.wrOp += 1; }
      else acc.none += 1;
    }
    return {
      ...acc,
      fsPct: (acc.fs / games) * 100,
      opPct: (acc.op / games) * 100,
      nonePct: (acc.none / games) * 100,
      wrWhenFs: acc.fs ? (acc.wrFs / acc.fs) * 100 : 0,
      wrWhenOp: acc.op ? (acc.wrOp / acc.op) * 100 : 0,
    };
  };

  const firstSlayer  = { ...summarizeFirst("firstSlayer") };
  firstSlayer.same   = rows.filter((g) => g.firstSlayer === "SAME").length;
  firstSlayer.samePct = (firstSlayer.same / games) * 100;
  const firstBlood   = summarizeFirst("firstBlood");
  const firstAbyssal = summarizeFirst("firstAbyssal");

  const insights = buildEarlyMacroInsights({ games, byMark, firstSlayer, firstBlood, firstAbyssal });

  return {
    games,
    byMark,
    firstSlayer,
    firstBlood,
    firstAbyssal,
    insights,
  };
}

export function aggregateEarlyMacroByOpponent(rows) {
  const byOpp = new Map();
  for (const g of rows) {
    const key = String(g.competitor || "").trim().toUpperCase();
    if (!key) continue;
    if (!byOpp.has(key)) byOpp.set(key, []);
    byOpp.get(key).push(g);
  }

  const out = [];
  for (const [opp, gs] of byOpp.entries()) {
    const agg = aggregateEarlyGameMacro(gs);
    const wins = gs.reduce((a, g) => a + (g.fsWin ? 1 : 0), 0);
    out.push({
      opponent: opp,
      games: gs.length,
      wins,
      wr: gs.length ? (wins / gs.length) * 100 : 0,
      deltaGold4:  agg.byMark?.[4]?.gold?.delta ?? 0,
      deltaTower8: agg.byMark?.[8]?.tower?.delta ?? 0,
      deltaObj12:  agg.byMark?.[12]?.objectives?.delta ?? 0,
      deltaObj15:  agg.byMark?.[15]?.objectives?.delta ?? 0,
      firstSlayerFsPct: agg.firstSlayer?.fsPct ?? 0,
      firstBloodFsPct:  agg.firstBlood?.fsPct ?? 0,
    });
  }
  return out.sort((a, b) => b.wr - a.wr || b.games - a.games);
}

function buildEarlyMacroInsights({ games, byMark, firstSlayer, firstBlood, firstAbyssal }) {
  const tips = [];
  if (!games) return tips;

  const pushIfClear = (cond, item) => { if (cond) tips.push(item); };

  // 1. Gold @ 4' — กำหนดเทรนด์เปิดเกม
  const g4 = byMark?.[4]?.gold;
  if (g4 && g4.sampled >= 3) {
    if (g4.delta >= 0.5) {
      pushIfClear(true, { tone: "good", title: `เปิดเกมเงินนำที่ 4' (+${g4.delta.toFixed(1)}k)`, text: `WR เมื่อ FS นำเงินที่ 4' = ${g4.wrWhenFsLead.toFixed(0)}% · จุดแข็งของทีม รักษาเทมโปนี้ไว้` });
    } else if (g4.delta <= -0.5) {
      pushIfClear(true, { tone: "bad", title: `เปิดเกมเงินตาม (${g4.delta.toFixed(1)}k @ 4')`, text: `FS lead เพียง ${g4.fsLeadPct.toFixed(0)}% ของเกม · ควรซ้อม early rotation/lane control` });
    }
  }

  // 2. First Slayer — จุดเทิร์น
  if (firstSlayer && (firstSlayer.fs + firstSlayer.op) >= 3) {
    if (firstSlayer.opPct >= 45) {
      pushIfClear(true, { tone: "bad", title: `เสีย First Slayer บ่อย (${firstSlayer.opPct.toFixed(0)}%)`, text: `OP ได้ slayer ตัวแรก ${firstSlayer.op}/${games} เกม · ควรตั้ง vision/setup รอบ slayer pit` });
    } else if (firstSlayer.fsPct >= 55 && firstSlayer.wrWhenFs >= 65) {
      pushIfClear(true, { tone: "good", title: `แย่ง First Slayer ได้ดี (${firstSlayer.fsPct.toFixed(0)}%)`, text: `WR เมื่อได้ first slayer = ${firstSlayer.wrWhenFs.toFixed(0)}% · ใช้ momentum ขยายเกม` });
    }
  }

  // 3. Tower @ 8' — จังหวะปั๊ม map control
  const t8 = byMark?.[8]?.tower;
  if (t8 && t8.sampled >= 3 && t8.delta >= 0.4) {
    pushIfClear(true, { tone: "good", title: `กดป้อมเก่งที่ 8' (Δ +${t8.delta.toFixed(1)})`, text: `เอา map pressure ออกได้เร็ว · ใช้ snowball เข้า objectives ตัวต่อไป` });
  } else if (t8 && t8.sampled >= 3 && t8.delta <= -0.4) {
    pushIfClear(true, { tone: "bad", title: `โดนถอนป้อมที่ 8' (Δ ${t8.delta.toFixed(1)})`, text: `WR เมื่อโดน OP lead ป้อม = ${(100 - t8.wrWhenFsLead - t8.wrWhenTie).toFixed(0)}% · ต้องเสริม wave management` });
  }

  // 4. Objectives @ 15' — closing
  const o15 = byMark?.[15]?.objectives;
  if (o15 && o15.sampled >= 3) {
    if (o15.delta >= 0.6) {
      pushIfClear(true, { tone: "good", title: `ปิดเกมด้วยออบเจ็คเหนือกว่า (Δ +${o15.delta.toFixed(1)} @ 15')`, text: `Late-game macro แข็งแรง รักษาแพทเทิร์น contest นี้ไว้` });
    } else if (o15.delta <= -0.4) {
      pushIfClear(true, { tone: "bad", title: `เสียออบเจ็คท้ายเกม (Δ ${o15.delta.toFixed(1)} @ 15')`, text: `แม้จะนำ early แต่หลุดที่ mid-game · ดู rotation/teamfight setup ช่วง 10-15'` });
    }
  }

  // 5. First Blood
  if (firstBlood && (firstBlood.fs + firstBlood.op) >= 3) {
    if (firstBlood.fsPct >= 60) {
      pushIfClear(true, { tone: "good", title: `กิน First Blood ได้บ่อย (${firstBlood.fsPct.toFixed(0)}%)`, text: `แสดงถึง early game aggression ดี · ใช้ tempo นี้กดต่อ` });
    } else if (firstBlood.opPct >= 60) {
      pushIfClear(true, { tone: "bad", title: `เสีย First Blood บ่อย (${firstBlood.opPct.toFixed(0)}%)`, text: `ระวัง 2' invade/lane gank · ทบทวน start position + ward` });
    }
  }

  if (tips.length === 0) {
    tips.push({ tone: "neutral", title: "Early game สูสี", text: "ค่าทุกอย่างใกล้เคียงคู่แข่ง · ไม่มีจุดเด่นหรือจุดอ่อนเด่นชัด" });
  }
  return tips;
}

/* ═══════════════════════════════════════════════════════════════
   FS TEAM REPORT — chart-first per-game extraction from All_Scrim
   ---------------------------------------------------------------
   Surfaces the rich per-role economy/combat/damage + side/length/
   week data that the Team Performance page turns into charts.
   FS perspective only (reads All_Scrim by exact header name, same
   approach as parseEarlyGameMacro). Other teams keep using the
   multi-team engine in teamPerformancePipeline.js.
   ═══════════════════════════════════════════════════════════════ */
const FS_REPORT_ROLES = ["DSL", "JUG", "MID", "ADL", "ROAM"];
// Header token per role differs between column families in the sheet:
//   economy/combat (Kill/Death/Assist/Gold/GPM): DSL/Jungle/Mid/ADL/Roaming
//   damage (… DMG / %… DMG):                     DSL/JUG/MID/ADL/ROAM
const FS_REPORT_ECON_TOKENS = { DSL: "dsl", JUG: "jungle", MID: "mid", ADL: "adl", ROAM: "roaming" };
const FS_REPORT_DMG_TOKENS = { DSL: "dsl", JUG: "jug", MID: "mid", ADL: "adl", ROAM: "roam" };

function _looksLikeDate(s) {
  const t = String(s ?? "").trim();
  return /^\d{8}$/.test(t) || /^\d{4}-\d{2}-\d{2}$/.test(t);
}

function _findFsReportIndex(headerRow) {
  // Build an exact normalized-header -> first column index lookup.
  const map = new Map();
  for (let i = 0; i < headerRow.length; i++) {
    const h = _normHeader(headerRow[i]);
    if (h && !map.has(h)) map.set(h, i);
  }
  const get = (h) => (map.has(h) ? map.get(h) : -1);

  const idx = {
    date: get("date"),
    week: get("week"),
    competitor: get("competitor"),
    side: get("fs side"),
    type: get("type"),
    fsWin: get("fs win"),
    matchWin: get("match win"),
    minute: get("minute"),
    killTotal: { fs: get("kill fs"), op: get("kill op") },
    allGpm: { fs: get("fs all gpm"), op: get("op all gpm") },
    roles: {},
  };

  for (const role of FS_REPORT_ROLES) {
    const econ = FS_REPORT_ECON_TOKENS[role];
    const dmg = FS_REPORT_DMG_TOKENS[role];
    idx.roles[role] = {};
    for (const side of ["fs", "op"]) {
      idx.roles[role][side] = {
        kill: get(`kill ${side} ${econ}`),
        death: get(`death ${side} ${econ}`),
        assist: get(`assist ${side} ${econ}`),
        gold: get(`gold ${side} ${econ}`),
        gpm: get(`gpm ${side} ${econ}`),
        // Raw per-role damage. NOTE the sheet's "%… DMG" columns are
        // actually damage-PER-MINUTE (mislabeled), so we compute the true
        // damage share ourselves from these raw totals in the aggregator.
        dmg: get(`${side} ${dmg} dmg`),
      };
    }
  }
  return idx;
}

export async function parseFsTeamReport(input) {
  const wb = await _toWorkbook(input);
  const sheetName = wb.Sheets["All_Scrim"] ? "All_Scrim" : (wb.Sheets["All_Scrim_Stats"] ? "All_Scrim_Stats" : null);
  const ws = sheetName ? wb.Sheets[sheetName] : null;
  if (!ws) return [];

  const rawArr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  const arr = constrainRowsToVisibleAllScrim(rawArr, sheetName);
  if (!arr.length) return [];

  const idx = _findFsReportIndex(arr[0] || []);
  if (idx.date < 0) return [];

  const rows = [];
  for (let r = 1; r < arr.length; r++) {
    const row = arr[r] || [];
    const date = String(row[idx.date] ?? "").trim();
    if (!date) continue;

    let competitor = idx.competitor >= 0 ? String(row[idx.competitor] ?? "").trim() : "";
    if (_looksLikeDate(competitor) || /^\d+$/.test(competitor)) competitor = "";

    const sideRaw = idx.side >= 0 ? String(row[idx.side] ?? "").trim() : "";
    const side = /^blue$/i.test(sideRaw) ? "Blue" : /^red$/i.test(sideRaw) ? "Red" : "";

    const type = idx.type >= 0 ? String(row[idx.type] ?? "").trim() : "";
    const bucket = /scrim/i.test(type) ? "scrim" : (type ? "official" : "");

    // FS win: prefer the 0/1 FS Win column; fall back to Match Win === "FS".
    let fsWin = 0;
    let fsWinKnown = false;
    if (idx.fsWin >= 0 && (Number(row[idx.fsWin]) === 0 || Number(row[idx.fsWin]) === 1)) {
      fsWin = Number(row[idx.fsWin]);
      fsWinKnown = true;
    } else if (idx.matchWin >= 0) {
      const mw = String(row[idx.matchWin] ?? "").trim().toUpperCase();
      if (mw) { fsWin = mw === "FS" ? 1 : 0; fsWinKnown = true; }
    }

    const minute = idx.minute >= 0 ? _numCell(row[idx.minute]) : null;

    const roles = {};
    let anyRole = false;
    for (const role of FS_REPORT_ROLES) {
      const made = {};
      for (const side2 of ["fs", "op"]) {
        const ci = idx.roles[role][side2];
        const cell = (k) => (ci[k] >= 0 ? _numCell(row[ci[k]]) : null);
        const o = {
          kill: cell("kill"), death: cell("death"), assist: cell("assist"),
          gold: cell("gold"), gpm: cell("gpm"), dmg: cell("dmg"),
        };
        if (o.kill != null || o.death != null || o.assist != null || o.gold != null || o.gpm != null || o.dmg != null) anyRole = true;
        made[side2] = o;
      }
      roles[role] = made;
    }

    const kills = {
      fs: idx.killTotal.fs >= 0 ? _numCell(row[idx.killTotal.fs]) : null,
      op: idx.killTotal.op >= 0 ? _numCell(row[idx.killTotal.op]) : null,
    };
    const totals = {
      fsAllGpm: idx.allGpm.fs >= 0 ? _numCell(row[idx.allGpm.fs]) : null,
      opAllGpm: idx.allGpm.op >= 0 ? _numCell(row[idx.allGpm.op]) : null,
    };

    // Keep only real game rows (drop empty trailing rows / spacers).
    if (!(anyRole || bucket || side || fsWinKnown)) continue;

    rows.push({
      date,
      week: idx.week >= 0 ? normalizeWeekValue(row[idx.week]) : "",
      competitor: competitor.toUpperCase(),
      side,
      bucket,
      type,
      fsWin,
      minute,
      roles,
      kills,
      totals,
    });
  }
  return rows;
}

export function parseFsTeamReportP(input) {
  return parseFsTeamReport(input);
}

function _emptyFsReport() {
  return {
    games: 0,
    side: { Blue: { games: 0, wins: 0, wr: 0 }, Red: { games: 0, wins: 0, wr: 0 } },
    buckets: { scrim: { games: 0, wins: 0, wr: 0 }, official: { games: 0, wins: 0, wr: 0 } },
    gameLength: { sampled: 0, avg: 0, median: 0, min: 0, max: 0, distribution: [] },
    trendByWeek: [],
    roles: [],
    rolesTotals: { fsAllGpm: 0, opAllGpm: 0 },
    kills: { fsAvg: 0, opAvg: 0 },
  };
}

export function aggregateFsTeamReport(rows) {
  const games = Array.isArray(rows) ? rows.length : 0;
  if (!games) return _emptyFsReport();

  const round = (v, d = 2) => (Number.isFinite(v) ? +v.toFixed(d) : 0);
  const wr = (wins, n) => (n > 0 ? round((wins / n) * 100, 1) : 0);

  // ── win record by side & bucket ──────────────────────────────
  const side = { Blue: { games: 0, wins: 0, wr: 0 }, Red: { games: 0, wins: 0, wr: 0 } };
  const buckets = { scrim: { games: 0, wins: 0, wr: 0 }, official: { games: 0, wins: 0, wr: 0 } };
  for (const g of rows) {
    if (side[g.side]) { side[g.side].games += 1; side[g.side].wins += g.fsWin ? 1 : 0; }
    if (buckets[g.bucket]) { buckets[g.bucket].games += 1; buckets[g.bucket].wins += g.fsWin ? 1 : 0; }
  }
  for (const k of ["Blue", "Red"]) side[k].wr = wr(side[k].wins, side[k].games);
  for (const k of ["scrim", "official"]) buckets[k].wr = wr(buckets[k].wins, buckets[k].games);

  // ── game length ──────────────────────────────────────────────
  const lengths = rows.map((g) => g.minute).filter((m) => Number.isFinite(m) && m > 0);
  const sorted = [...lengths].sort((a, b) => a - b);
  const sampled = sorted.length;
  const sum = sorted.reduce((a, b) => a + b, 0);
  const median = sampled ? (sampled % 2 ? sorted[(sampled - 1) / 2] : (sorted[sampled / 2 - 1] + sorted[sampled / 2]) / 2) : 0;
  const BINS = [
    { bucket: "<12'", test: (m) => m < 12 },
    { bucket: "12–16'", test: (m) => m >= 12 && m < 16 },
    { bucket: "16–20'", test: (m) => m >= 16 && m < 20 },
    { bucket: "20–25'", test: (m) => m >= 20 && m < 25 },
    { bucket: "≥25'", test: (m) => m >= 25 },
  ];
  const distribution = BINS.map((b) => {
    const inBin = rows.filter((g) => Number.isFinite(g.minute) && g.minute > 0 && b.test(g.minute));
    const wins = inBin.reduce((a, g) => a + (g.fsWin ? 1 : 0), 0);
    return { bucket: b.bucket, games: inBin.length, wins, wr: wr(wins, inBin.length) };
  });
  const gameLength = {
    sampled,
    avg: sampled ? round(sum / sampled, 1) : 0,
    median: round(median, 1),
    min: sampled ? round(sorted[0], 1) : 0,
    max: sampled ? round(sorted[sampled - 1], 1) : 0,
    distribution,
  };

  // ── win-rate trend by week ───────────────────────────────────
  const weekMap = new Map();
  for (const g of rows) {
    const w = g.week || "—";
    const cur = weekMap.get(w) || { week: w, games: 0, wins: 0, scrimGames: 0, scrimWins: 0, offGames: 0, offWins: 0 };
    cur.games += 1; cur.wins += g.fsWin ? 1 : 0;
    if (g.bucket === "scrim") { cur.scrimGames += 1; cur.scrimWins += g.fsWin ? 1 : 0; }
    else if (g.bucket === "official") { cur.offGames += 1; cur.offWins += g.fsWin ? 1 : 0; }
    weekMap.set(w, cur);
  }
  const weekNum = (w) => { const m = String(w).match(/(\d+)/); return m ? Number(m[1]) : Number.POSITIVE_INFINITY; };
  const trendByWeek = [...weekMap.values()]
    .sort((a, b) => weekNum(a.week) - weekNum(b.week) || String(a.week).localeCompare(String(b.week)))
    .map((c) => ({
      week: c.week,
      games: c.games,
      wins: c.wins,
      wr: wr(c.wins, c.games),
      scrimWr: c.scrimGames ? wr(c.scrimWins, c.scrimGames) : null,
      officialWr: c.offGames ? wr(c.offWins, c.offGames) : null,
    }));

  // ── per-role FS vs OP averages ───────────────────────────────
  const FIELDS = ["kill", "death", "assist", "gold", "gpm", "dmg"];
  const sideAvg = (roleKey, s) => {
    const acc = {}; const n = {};
    FIELDS.forEach((f) => { acc[f] = 0; n[f] = 0; });
    let rowsN = 0;
    for (const g of rows) {
      const o = g.roles?.[roleKey]?.[s];
      if (!o) continue;
      let touched = false;
      for (const f of FIELDS) {
        const v = o[f];
        if (Number.isFinite(v)) { acc[f] += v; n[f] += 1; touched = true; }
      }
      if (touched) rowsN += 1;
    }
    const avg = (f) => (n[f] ? acc[f] / n[f] : null);
    const kill = avg("kill"); const death = avg("death"); const assist = avg("assist");
    const kda = (kill != null && death != null && assist != null) ? round((kill + assist) / Math.max(1, death), 2) : null;
    return {
      kill: kill != null ? round(kill, 2) : null,
      death: death != null ? round(death, 2) : null,
      assist: assist != null ? round(assist, 2) : null,
      kda,
      gpm: avg("gpm") != null ? round(avg("gpm"), 0) : null,
      gold: avg("gold") != null ? round(avg("gold"), 0) : null,
      dmg: avg("dmg") != null ? round(avg("dmg"), 0) : null,
      n: rowsN,
    };
  };

  // Damage SHARE per role = sum(raw role dmg) / sum(all-role dmg) × 100.
  // Computed from raw "FS/OP {role} DMG" totals (NOT the mislabeled "%…"
  // columns, which are per-minute), so the 5 roles sum to ~100% per side.
  const dmgSum = { fs: {}, op: {} };
  FS_REPORT_ROLES.forEach((r) => { dmgSum.fs[r] = 0; dmgSum.op[r] = 0; });
  for (const g of rows) {
    for (const s of ["fs", "op"]) {
      for (const r of FS_REPORT_ROLES) {
        const v = g.roles?.[r]?.[s]?.dmg;
        if (Number.isFinite(v)) dmgSum[s][r] += v;
      }
    }
  }
  const dmgTotal = { fs: FS_REPORT_ROLES.reduce((a, r) => a + dmgSum.fs[r], 0), op: FS_REPORT_ROLES.reduce((a, r) => a + dmgSum.op[r], 0) };
  const dmgShare = (s, r) => (dmgTotal[s] > 0 ? round((dmgSum[s][r] / dmgTotal[s]) * 100, 1) : null);

  const dlt = (a, b) => (a != null && b != null ? round(a - b, 2) : null);
  const roles = FS_REPORT_ROLES.map((roleKey) => {
    const fs = sideAvg(roleKey, "fs"); fs.dmgShare = dmgShare("fs", roleKey);
    const op = sideAvg(roleKey, "op"); op.dmgShare = dmgShare("op", roleKey);
    return {
      role: roleKey,
      fs,
      op,
      delta: { kda: dlt(fs.kda, op.kda), gpm: dlt(fs.gpm, op.gpm), gold: dlt(fs.gold, op.gold), dmgShare: dlt(fs.dmgShare, op.dmgShare) },
    };
  });

  // ── team totals & headline kills ─────────────────────────────
  const avgField = (pick) => {
    let s = 0; let n = 0;
    for (const g of rows) { const v = pick(g); if (Number.isFinite(v)) { s += v; n += 1; } }
    return n ? s / n : 0;
  };
  const rolesTotals = {
    fsAllGpm: round(avgField((g) => g.totals?.fsAllGpm), 0),
    opAllGpm: round(avgField((g) => g.totals?.opAllGpm), 0),
  };
  const kills = {
    fsAvg: round(avgField((g) => g.kills?.fs), 1),
    opAvg: round(avgField((g) => g.kills?.op), 1),
  };

  return { games, side, buckets, gameLength, trendByWeek, roles, rolesTotals, kills };
}
